// Easyflex operator manual: `docs/easyflex-manual/README.md`
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { RootState } from '../store';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Tab,
  Tabs,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { centralDbRequest } from '../services/centralDbClient';
import { useSyncEventStream, SyncEvent, SyncPhaseCompleteEvent } from '../hooks/useSyncEventStream';

type EasyflexTabKey = 'dashboard' | 'jobs' | 'logs' | 'cache' | 'collisions' | 'freeze' | 'tools';

function isEasyflexTabKey(value: string | null): value is EasyflexTabKey {
  return (
    value === 'dashboard' ||
    value === 'jobs' ||
    value === 'logs' ||
    value === 'cache' ||
    value === 'collisions' ||
    value === 'freeze' ||
    value === 'tools'
  );
}

function a11yTabProps(key: EasyflexTabKey) {
  return {
    id: `easyflex-tab-${key}`,
    'aria-controls': `easyflex-tabpanel-${key}`,
  };
}

const jsonStringify = (v: any) => {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
};

const getIsoWeekInfo = (input?: Date): { weekYear: number; weekNumber: number } => {
  const d = input ? new Date(input) : new Date();
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

  // ISO week date weeks start on Monday, so correct the day number
  const dayNr = (date.getUTCDay() + 6) % 7;
  // Set to the Thursday of the current week: week year is the year of that Thursday
  date.setUTCDate(date.getUTCDate() - dayNr + 3);

  const weekYear = date.getUTCFullYear();
  const week1 = new Date(Date.UTC(weekYear, 0, 4));
  const week1DayNr = (week1.getUTCDay() + 6) % 7;
  week1.setUTCDate(week1.getUTCDate() - week1DayNr + 3);

  const weekNumber = 1 + Math.round((date.getTime() - week1.getTime()) / (7 * 24 * 60 * 60 * 1000));
  return { weekYear, weekNumber };
};

type ActieflijstSyncRun = {
  success: boolean;
  syncRunId: string;
  startedAt: string;
  finishedAt?: string | null;
  durationMs?: number | null;
  hoursWeekYear: number;
  hoursWeekNumber: number;
  totalActivePlacements?: number | null;
  created?: number | null;
  updated?: number | null;
  archived?: number | null;
  skipped?: number | null;
  errors?: number | null;
  errorMessage?: string | null;
  breakdown?: any;
};

type ActieflijstRefreshJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED';

type ActieflijstRefreshJob = {
  id?: string | number;
  status: ActieflijstRefreshJobStatus;
  syncRunId?: string | null;
  fromWeekYear: number;
  fromWeekNumber: number;
  toWeekYear: number;
  toWeekNumber: number;
  missingOnly?: boolean | null;
  totalWeeks?: number | null;
  completedWeeks?: number | null;
  skippedWeeks?: number | null;
  currentWeek?: { weekYear: number; weekNumber: number } | string | null;
  results?: any;
  errorMessage?: string | null;
};

type DebugLogEntry = {
  ts: string;
  kind: 'REQ' | 'RES' | 'ERR' | 'INFO';
  message: string;
  data?: any;
};

const jobStatusChipColor = (status: ActieflijstRefreshJobStatus): 'default' | 'warning' | 'success' | 'error' => {
  if (status === 'RUNNING' || status === 'PENDING') return 'warning';
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'error';
  return 'default';
};

const SaleslistActieflijstWeekSyncSection: React.FC<{
  accessToken?: string | null;
  busy: boolean;
  run: (fn: () => Promise<any>) => void;
}> = ({ accessToken, busy, run }) => {
  const nowIso = useMemo(() => getIsoWeekInfo(), []);

  const [manualYear, setManualYear] = useState<string>(String(nowIso.weekYear));
  const [manualWeek, setManualWeek] = useState<string>(String(nowIso.weekNumber));

  const [rangeFromYear, setRangeFromYear] = useState<string>(String(nowIso.weekYear));
  const [rangeFromWeek, setRangeFromWeek] = useState<string>(String(nowIso.weekNumber));
  const [rangeToYear, setRangeToYear] = useState<string>(String(nowIso.weekYear));
  const [rangeToWeek, setRangeToWeek] = useState<string>(String(nowIso.weekNumber));
  const [rangeMissingOnly, setRangeMissingOnly] = useState<boolean>(true);

  const [refreshJobs, setRefreshJobs] = useState<ActieflijstRefreshJob[] | null>(null);
  const [refreshJobsLoading, setRefreshJobsLoading] = useState<boolean>(false);
  const [refreshJobsError, setRefreshJobsError] = useState<string | null>(null);
  const [refreshJobsAutoRefresh, setRefreshJobsAutoRefresh] = useState<boolean>(true);
  const [refreshJobsPollSeconds, setRefreshJobsPollSeconds] = useState<number>(2);
  const [refreshJobsLastUpdatedAt, setRefreshJobsLastUpdatedAt] = useState<string>('');

  const [selectedRefreshJobId, setSelectedRefreshJobId] = useState<string>('');
  const [selectedRefreshJob, setSelectedRefreshJob] = useState<ActieflijstRefreshJob | null>(null);
  const [selectedRefreshJobLoading, setSelectedRefreshJobLoading] = useState<boolean>(false);
  const [selectedRefreshJobError, setSelectedRefreshJobError] = useState<string | null>(null);
  const [selectedRefreshJobAutoTrack, setSelectedRefreshJobAutoTrack] = useState<boolean>(true);
  const [showSelectedRefreshJobRaw, setShowSelectedRefreshJobRaw] = useState<boolean>(false);

  const [syncRuns, setSyncRuns] = useState<ActieflijstSyncRun[] | null>(null);
  const [syncRunsLoading, setSyncRunsLoading] = useState<boolean>(false);
  const [syncRunsError, setSyncRunsError] = useState<string | null>(null);
  const [syncRunsLimit, setSyncRunsLimit] = useState<number>(50);
  const [syncRunsFromSyncRunId, setSyncRunsFromSyncRunId] = useState<string>('');
  const [selectedSyncRun, setSelectedSyncRun] = useState<ActieflijstSyncRun | null>(null);
  const [showSelectedSyncRunRaw, setShowSelectedSyncRunRaw] = useState<boolean>(false);

  const [audit, setAudit] = useState<any | null>(null);
  const [auditLoading, setAuditLoading] = useState<boolean>(false);
  const [auditError, setAuditError] = useState<string | null>(null);
  const [showAuditRaw, setShowAuditRaw] = useState<boolean>(false);

  const [reconcileYear, setReconcileYear] = useState<string>(String(nowIso.weekYear));
  const [reconcileWeek, setReconcileWeek] = useState<string>(String(nowIso.weekNumber));
  const [reconcileLoading, setReconcileLoading] = useState<boolean>(false);
  const [reconcileError, setReconcileError] = useState<string | null>(null);
  const [reconcileResult, setReconcileResult] = useState<any | null>(null);
  const [showReconcileRaw, setShowReconcileRaw] = useState<boolean>(true);

  const [debugLogs, setDebugLogs] = useState<DebugLogEntry[]>([]);
  const [debugVerbose, setDebugVerbose] = useState<boolean>(false);
  const debugLogRef = useRef<HTMLDivElement>(null);

  const pushLog = (entry: DebugLogEntry) => {
    setDebugLogs(prev => [...prev, entry].slice(-300));
  };

  useEffect(() => {
    if (debugLogRef.current && debugLogs.length) {
      debugLogRef.current.scrollTop = debugLogRef.current.scrollHeight;
    }
  }, [debugLogs]);

  const request = async (options: Parameters<typeof centralDbRequest>[0]) => {
    const query = options?.query ?? undefined;
    const body = options?.body ?? undefined;
    pushLog({
      ts: new Date().toISOString(),
      kind: 'REQ',
      message: `${String(options?.method || 'GET').toUpperCase()} ${String(options?.path || '')}`,
      data: { query, body },
    });
    const res = await centralDbRequest({ ...options, token: accessToken });
    pushLog({
      ts: new Date().toISOString(),
      kind: res.ok ? 'RES' : 'ERR',
      message: `${res.status} ${String(options?.method || 'GET').toUpperCase()} ${String(options?.path || '')}`,
      data: res.data,
    });
    return res;
  };

  const parseYear = (value: string, label: string) => {
    const year = Number(String(value || '').trim());
    if (!Number.isFinite(year) || year < 2000 || year > 2100) {
      throw new Error(`${label} must be a valid year (2000–2100)`);
    }
    return Math.trunc(year);
  };

  const parseWeek = (value: string, label: string) => {
    const week = Number(String(value || '').trim());
    if (!Number.isFinite(week) || week < 1 || week > 53) {
      throw new Error(`${label} must be a valid week number (1–53)`);
    }
    return Math.trunc(week);
  };

  const getJobId = (job: any): string => {
    const id = job?.id ?? job?.jobId ?? job?._id ?? '';
    return String(id || '').trim();
  };

  const fetchRefreshJobs = async () => {
    setRefreshJobsLoading(true);
    setRefreshJobsError(null);
    try {
      const res = await request({ method: 'GET', path: '/saleslist/actieflijst/refresh-jobs' });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      const items = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
      setRefreshJobs(items);
      setRefreshJobsLastUpdatedAt(new Date().toLocaleString());
    } catch (e: any) {
      setRefreshJobsError(e?.message || 'Failed to load refresh jobs');
      setRefreshJobs(null);
    } finally {
      setRefreshJobsLoading(false);
    }
  };

  const fetchRefreshJobById = async (id: string) => {
    const safeId = String(id || '').trim();
    if (!safeId) {
      setSelectedRefreshJob(null);
      return;
    }
    setSelectedRefreshJobLoading(true);
    setSelectedRefreshJobError(null);
    try {
      const res = await request({
        method: 'GET',
        path: `/saleslist/actieflijst/refresh-jobs/${encodeURIComponent(safeId)}`,
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setSelectedRefreshJob(res.data ?? null);
    } catch (e: any) {
      setSelectedRefreshJobError(e?.message || 'Failed to load job status');
      setSelectedRefreshJob(null);
    } finally {
      setSelectedRefreshJobLoading(false);
    }
  };

  const fetchSyncRuns = async (options?: { limit?: number; fromSyncRunId?: string }) => {
    setSyncRunsLoading(true);
    setSyncRunsError(null);
    try {
      const limit = Math.max(1, Math.min(500, Number(options?.limit ?? syncRunsLimit ?? 50)));
      const fromSyncRunId = String(options?.fromSyncRunId ?? syncRunsFromSyncRunId ?? '').trim();
      const res = await request({
        method: 'GET',
        path: '/saleslist/actieflijst/sync-runs',
        query: { limit, syncRunId: fromSyncRunId || undefined },
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      const items = Array.isArray(res.data?.items) ? res.data.items : Array.isArray(res.data) ? res.data : [];
      setSyncRuns(items);
    } catch (e: any) {
      setSyncRunsError(e?.message || 'Failed to load sync history');
      setSyncRuns(null);
    } finally {
      setSyncRunsLoading(false);
    }
  };

  const fetchAudit = async () => {
    setAuditLoading(true);
    setAuditError(null);
    try {
      const res = await request({ method: 'GET', path: '/saleslist/actieflijst/sync-audit' });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setAudit(res.data ?? null);
    } catch (e: any) {
      setAuditError(e?.message || 'Failed to load sync audit');
      setAudit(null);
    } finally {
      setAuditLoading(false);
    }
  };

  const fetchReconcileHours = async (year: number, week: number) => {
    setReconcileLoading(true);
    setReconcileError(null);
    try {
      const res = await request({
        method: 'GET',
        path: '/saleslist/actieflijst/reconcile-hours',
        query: { year, week },
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setReconcileResult(res.data ?? null);
    } catch (e: any) {
      setReconcileError(e?.message || 'Failed to reconcile hours');
      setReconcileResult(null);
    } finally {
      setReconcileLoading(false);
    }
  };

  useEffect(() => {
    fetchRefreshJobs();
    fetchSyncRuns({ limit: syncRunsLimit, fromSyncRunId: syncRunsFromSyncRunId });
    fetchAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    const pollSeconds = Math.max(1, Math.min(60, Number(refreshJobsPollSeconds || 2)));
    const anyRunning = Array.isArray(refreshJobs) && refreshJobs.some(j => ['PENDING', 'RUNNING'].includes(String(j?.status)));
    if (!refreshJobsAutoRefresh && !anyRunning) return;
    const t = window.setInterval(() => fetchRefreshJobs(), pollSeconds * 1000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshJobsAutoRefresh, refreshJobs, refreshJobsPollSeconds]);

  useEffect(() => {
    const job = selectedRefreshJob;
    const id = String(selectedRefreshJobId || '').trim();
    if (!id) return;
    if (!selectedRefreshJobAutoTrack) return;
    const status = String(job?.status || '');
    if (status !== 'RUNNING' && status !== 'PENDING') return;
    const pollSeconds = Math.max(1, Math.min(60, Number(refreshJobsPollSeconds || 2)));
    const t = window.setInterval(() => fetchRefreshJobById(id), pollSeconds * 1000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefreshJobId, selectedRefreshJob, selectedRefreshJobAutoTrack, refreshJobsPollSeconds]);

  const jobsStatusSummary = useMemo(() => {
    const items = Array.isArray(refreshJobs) ? refreshJobs : [];
    const counts: Record<string, number> = { PENDING: 0, RUNNING: 0, COMPLETED: 0, FAILED: 0 };
    for (const j of items) {
      const s = String(j?.status || '');
      if (counts[s] !== undefined) counts[s] += 1;
    }
    return counts;
  }, [refreshJobs]);

  return (
    <Stack spacing={2}>
      <Typography variant="h6" fontWeight={800}>
        Saleslist / Actieflijst Week Sync
      </Typography>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Manual week sync
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setManualYear(String(nowIso.weekYear));
                    setManualWeek(String(nowIso.weekNumber));
                  }}
                  disabled={busy}
                >
                  This week
                </Button>
              </Stack>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              Triggers a manual Actieflijst → Saleslist sync for a specific ISO week.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="Year"
                value={manualYear}
                onChange={e => setManualYear(e.target.value)}
                sx={{ width: { md: 160 } }}
              />
              <TextField
                label="Week"
                value={manualWeek}
                onChange={e => setManualWeek(e.target.value)}
                sx={{ width: { md: 160 } }}
              />
              <Button
                variant="contained"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const year = parseYear(manualYear, 'Year');
                    const week = parseWeek(manualWeek, 'Week');
                    const res = await request({
                      method: 'POST',
                      path: '/saleslist/actieflijst/sync',
                      query: { year, week },
                    });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                    await fetchSyncRuns({ limit: syncRunsLimit, fromSyncRunId: syncRunsFromSyncRunId });
                    await fetchAudit();
                    return res.data;
                  })
                }
              >
                Sync week
              </Button>
              <Tooltip title="Debug endpoint: reconcile missing hours for this week" children={
                <span>
                  <Button
                    variant="outlined"
                    disabled={busy || reconcileLoading}
                    onClick={async () => {
                      try {
                        const year = parseYear(manualYear, 'Year');
                        const week = parseWeek(manualWeek, 'Week');
                        setReconcileYear(String(year));
                        setReconcileWeek(String(week));
                        await fetchReconcileHours(year, week);
                      } catch (e: any) {
                        setReconcileError(e?.message || 'Invalid year/week');
                        setReconcileResult(null);
                      }
                    }}
                  >
                    Reconcile hours
                  </Button>
                </span>
              } />
            </Stack>

            {reconcileError ? <Alert severity="error">{reconcileError}</Alert> : null}
            {reconcileLoading ? <LinearProgress /> : null}

            {reconcileResult && showReconcileRaw ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" color="text.secondary">
                      Reconcile result ({reconcileYear}-W{reconcileWeek})
                    </Typography>
                    <FormControlLabel
                      control={<Checkbox checked={showReconcileRaw} onChange={e => setShowReconcileRaw(e.target.checked)} />}
                      label="Show raw"
                    />
                  </Stack>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(reconcileResult)}</pre>
                </CardContent>
              </Card>
            ) : (
              <FormControlLabel
                control={<Checkbox checked={showReconcileRaw} onChange={e => setShowReconcileRaw(e.target.checked)} />}
                label="Show reconcile raw JSON"
              />
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              Refresh weeks (range job)
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Starts a background job to refresh multiple week ranges. Use “missing only” to skip weeks that already have complete data.
            </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="From year" value={rangeFromYear} onChange={e => setRangeFromYear(e.target.value)} fullWidth />
              <TextField label="From week" value={rangeFromWeek} onChange={e => setRangeFromWeek(e.target.value)} fullWidth />
              <TextField label="To year" value={rangeToYear} onChange={e => setRangeToYear(e.target.value)} fullWidth />
              <TextField label="To week" value={rangeToWeek} onChange={e => setRangeToWeek(e.target.value)} fullWidth />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={rangeMissingOnly} onChange={e => setRangeMissingOnly(e.target.checked)} />}
                label="Missing only"
              />
              <Button
                variant="contained"
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    const fromWeekYear = parseYear(rangeFromYear, 'From year');
                    const fromWeekNumber = parseWeek(rangeFromWeek, 'From week');
                    const toWeekYear = parseYear(rangeToYear, 'To year');
                    const toWeekNumber = parseWeek(rangeToWeek, 'To week');

                    const res = await request({
                      method: 'POST',
                      path: '/saleslist/actieflijst/refresh-weeks',
                      body: { fromWeekYear, fromWeekNumber, toWeekYear, toWeekNumber, missingOnly: rangeMissingOnly },
                    });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

                    await fetchRefreshJobs();
                    const createdId = getJobId(res.data);
                    if (createdId) {
                      setSelectedRefreshJobId(createdId);
                      await fetchRefreshJobById(createdId);
                    }
                    return res.data;
                  })
                }
              >
                Start refresh job
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Refresh jobs (live)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" onClick={() => fetchRefreshJobs()} disabled={refreshJobsLoading}>
                  Refresh
                </Button>
              </Stack>
            </Stack>

            {refreshJobsError ? <Alert severity="error">{refreshJobsError}</Alert> : null}
            {refreshJobsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={refreshJobsAutoRefresh} onChange={e => setRefreshJobsAutoRefresh(e.target.checked)} />}
                label="Auto-refresh job list"
              />
              <TextField
                label="Poll interval (seconds)"
                value={String(refreshJobsPollSeconds)}
                onChange={e => setRefreshJobsPollSeconds(Math.max(1, Math.min(60, Number(e.target.value || 2))))}
                sx={{ width: { md: 220 } }}
              />
              {refreshJobsLastUpdatedAt ? (
                <Typography variant="caption" color="text.secondary">
                  last updated: {refreshJobsLastUpdatedAt}
                </Typography>
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip size="small" color="warning" label={`PENDING ${jobsStatusSummary.PENDING}`} variant="outlined" />
              <Chip size="small" color="warning" label={`RUNNING ${jobsStatusSummary.RUNNING}`} variant="outlined" />
              <Chip size="small" color="success" label={`COMPLETED ${jobsStatusSummary.COMPLETED}`} variant="outlined" />
              <Chip size="small" color="error" label={`FAILED ${jobsStatusSummary.FAILED}`} variant="outlined" />
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Typography variant="subtitle2" color="text.secondary">
                Selected job
              </Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  label="Job id"
                  value={selectedRefreshJobId}
                  onChange={e => setSelectedRefreshJobId(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => fetchRefreshJobById(selectedRefreshJobId)}
                  disabled={selectedRefreshJobLoading || !selectedRefreshJobId.trim()}
                >
                  Load
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setSelectedRefreshJobId('');
                    setSelectedRefreshJob(null);
                    setSelectedRefreshJobError(null);
                  }}
                  disabled={selectedRefreshJobLoading}
                >
                  Clear
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <FormControlLabel
                  control={<Checkbox checked={selectedRefreshJobAutoTrack} onChange={e => setSelectedRefreshJobAutoTrack(e.target.checked)} />}
                  label="Auto-track if RUNNING"
                />
                <FormControlLabel
                  control={<Checkbox checked={showSelectedRefreshJobRaw} onChange={e => setShowSelectedRefreshJobRaw(e.target.checked)} />}
                  label="Show raw JSON"
                />
              </Stack>

              {selectedRefreshJobError ? <Alert severity="error">{selectedRefreshJobError}</Alert> : null}
              {selectedRefreshJobLoading ? <LinearProgress /> : null}

              {selectedRefreshJob ? (
                <Card variant="outlined">
                  <CardContent>
                    <Stack spacing={1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            color={jobStatusChipColor(selectedRefreshJob.status)}
                            label={selectedRefreshJob.status}
                            variant="filled"
                          />
                          <Typography variant="body2" fontWeight={700}>
                            {getJobId(selectedRefreshJob) ? `job ${getJobId(selectedRefreshJob)}` : 'job'}
                          </Typography>
                          {selectedRefreshJob.missingOnly ? <Chip size="small" label="missingOnly" variant="outlined" /> : null}
                        </Stack>
                        {selectedRefreshJob.syncRunId ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSyncRunsFromSyncRunId(String(selectedRefreshJob.syncRunId || ''));
                              fetchSyncRuns({ fromSyncRunId: String(selectedRefreshJob.syncRunId || ''), limit: syncRunsLimit });
                            }}
                          >
                            Filter history by syncRunId
                          </Button>
                        ) : null}
                      </Stack>

                      <Typography variant="caption" color="text.secondary">
                        range: {selectedRefreshJob.fromWeekYear}-W{selectedRefreshJob.fromWeekNumber} → {selectedRefreshJob.toWeekYear}-W{selectedRefreshJob.toWeekNumber}
                      </Typography>

                      {typeof selectedRefreshJob.totalWeeks === 'number' ? (
                        <Stack spacing={0.5}>
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">
                              progress: {Number(selectedRefreshJob.completedWeeks ?? 0)}/{Number(selectedRefreshJob.totalWeeks ?? 0)} completed
                            </Typography>
                            {typeof selectedRefreshJob.skippedWeeks === 'number' ? (
                              <Chip size="small" label={`skipped ${selectedRefreshJob.skippedWeeks}`} variant="outlined" />
                            ) : null}
                            {selectedRefreshJob.currentWeek ? (
                              <Chip
                                size="small"
                                label={`current ${typeof selectedRefreshJob.currentWeek === 'string' ? selectedRefreshJob.currentWeek : `${selectedRefreshJob.currentWeek.weekYear}-W${selectedRefreshJob.currentWeek.weekNumber}`}`}
                                variant="outlined"
                              />
                            ) : null}
                          </Stack>
                          <LinearProgress
                            variant="determinate"
                            value={
                              Number(selectedRefreshJob.totalWeeks ?? 0) > 0
                                ? Math.min(
                                    100,
                                    Math.max(
                                      0,
                                      (Number(selectedRefreshJob.completedWeeks ?? 0) / Number(selectedRefreshJob.totalWeeks ?? 0)) * 100,
                                    ),
                                  )
                                : 0
                            }
                          />
                        </Stack>
                      ) : null}

                      {selectedRefreshJob.errorMessage ? <Alert severity="error">{String(selectedRefreshJob.errorMessage)}</Alert> : null}

                      {showSelectedRefreshJobRaw ? (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(selectedRefreshJob)}</pre>
                      ) : null}
                    </Stack>
                  </CardContent>
                </Card>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No job selected.
                </Typography>
              )}
            </Stack>

            <Divider />

            {Array.isArray(refreshJobs) ? (
              <Stack spacing={1}>
                {refreshJobs.length ? (
                  refreshJobs.map((job, idx) => {
                    const id = getJobId(job) || String(idx);
                    const status = String(job?.status || 'PENDING') as ActieflijstRefreshJobStatus;
                    const totalWeeks = Number(job?.totalWeeks ?? 0);
                    const completedWeeks = Number(job?.completedWeeks ?? 0);
                    const progressPct = totalWeeks > 0 ? Math.round((completedWeeks / totalWeeks) * 100) : undefined;
                    const rangeLabel = `${job?.fromWeekYear}-W${job?.fromWeekNumber} → ${job?.toWeekYear}-W${job?.toWeekNumber}`;

                    return (
                      <Card key={id} variant="outlined" sx={{ borderStyle: status === 'RUNNING' ? 'solid' : 'dashed' }}>
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip size="small" color={jobStatusChipColor(status)} label={status} variant="filled" />
                                <Typography variant="subtitle2" fontWeight={800}>
                                  {id ? `job ${id}` : 'job'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {rangeLabel}
                                </Typography>
                                {job?.missingOnly ? <Chip size="small" label="missingOnly" variant="outlined" /> : null}
                                {Number.isFinite(progressPct) ? <Chip size="small" label={`${progressPct}%`} variant="outlined" /> : null}
                              </Stack>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  if (!id) return;
                                  setSelectedRefreshJobId(id);
                                  fetchRefreshJobById(id);
                                }}
                              >
                                Inspect
                              </Button>
                            </Stack>

                            {totalWeeks > 0 ? (
                              <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, (completedWeeks / totalWeeks) * 100))} />
                            ) : null}

                            {job?.errorMessage ? (
                              <Typography variant="caption" color="error">
                                {String(job.errorMessage).slice(0, 240)}
                              </Typography>
                            ) : null}

                            {job?.syncRunId ? (
                              <Typography variant="caption" color="text.secondary">
                                syncRunId: {String(job.syncRunId)}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No refresh jobs found.
                  </Typography>
                )}
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Sync history
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSelectedSyncRun(null);
                  fetchSyncRuns({ limit: syncRunsLimit, fromSyncRunId: syncRunsFromSyncRunId });
                }}
                disabled={syncRunsLoading}
              >
                Refresh
              </Button>
            </Stack>

            {syncRunsError ? <Alert severity="error">{syncRunsError}</Alert> : null}
            {syncRunsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="Limit"
                value={String(syncRunsLimit)}
                onChange={e => setSyncRunsLimit(Math.max(1, Math.min(500, Number(e.target.value || 50))))}
                sx={{ width: { md: 160 } }}
              />
              <TextField
                label="From syncRunId (optional)"
                value={syncRunsFromSyncRunId}
                onChange={e => setSyncRunsFromSyncRunId(e.target.value)}
                fullWidth
              />
              <Button
                variant="outlined"
                onClick={() => fetchSyncRuns({ limit: syncRunsLimit, fromSyncRunId: syncRunsFromSyncRunId })}
                disabled={syncRunsLoading}
              >
                Apply
              </Button>
            </Stack>

            {Array.isArray(syncRuns) ? (
              <Stack spacing={1}>
                {syncRuns.length ? (
                  syncRuns.map((r, idx) => {
                    const syncRunId = String(r?.syncRunId || idx);
                    const success = Boolean(r?.success);
                    const startedAt = r?.startedAt ? new Date(r.startedAt).toLocaleString() : '—';
                    const finishedAt = r?.finishedAt ? new Date(r.finishedAt).toLocaleString() : '—';
                    const weekLabel = `${r?.hoursWeekYear}-W${r?.hoursWeekNumber}`;
                    const duration = typeof r?.durationMs === 'number' ? `${Math.round(r.durationMs / 1000)}s` : '—';

                    const created = Number(r?.created ?? 0);
                    const updated = Number(r?.updated ?? 0);
                    const archived = Number(r?.archived ?? 0);
                    const skipped = Number(r?.skipped ?? 0);
                    const errors = Number(r?.errors ?? 0);

                    return (
                      <Card key={syncRunId} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip size="small" color={success ? 'success' : 'error'} label={success ? 'SUCCESS' : 'FAILED'} variant="filled" />
                                <Chip size="small" label={weekLabel} variant="outlined" />
                                <Typography variant="caption" color="text.secondary">
                                  {startedAt} → {finishedAt} ({duration})
                                </Typography>
                                <Chip size="small" label={`created ${created}`} variant="outlined" />
                                <Chip size="small" label={`updated ${updated}`} variant="outlined" />
                                <Chip size="small" label={`archived ${archived}`} variant="outlined" />
                                <Chip size="small" label={`skipped ${skipped}`} variant="outlined" />
                                {errors ? <Chip size="small" color="error" label={`errors ${errors}`} variant="outlined" /> : null}
                              </Stack>
                              <Button size="small" variant="outlined" onClick={() => setSelectedSyncRun(r)}>
                                Details
                              </Button>
                            </Stack>

                            <Typography variant="caption" color="text.secondary">
                              syncRunId: {syncRunId}
                            </Typography>

                            {r?.errorMessage ? (
                              <Typography variant="caption" color="error">
                                {String(r.errorMessage).slice(0, 260)}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No sync runs found.
                  </Typography>
                )}
              </Stack>
            ) : null}

            {selectedSyncRun ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Selected sync run
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FormControlLabel
                        control={<Checkbox checked={showSelectedSyncRunRaw} onChange={e => setShowSelectedSyncRunRaw(e.target.checked)} />}
                        label="Show raw JSON"
                      />
                      <Button size="small" variant="text" onClick={() => setSelectedSyncRun(null)}>
                        Clear
                      </Button>
                    </Stack>
                  </Stack>

                  <Collapse in={!showSelectedSyncRunRaw}>
                    {selectedSyncRun.breakdown ? (
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            Breakdown
                          </Typography>
                          <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {jsonStringify(selectedSyncRun.breakdown)}
                          </pre>
                        </CardContent>
                      </Card>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No breakdown field.
                      </Typography>
                    )}
                  </Collapse>

                  <Collapse in={showSelectedSyncRunRaw}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {jsonStringify(selectedSyncRun)}
                    </pre>
                  </Collapse>
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Sync audit dashboard
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button variant="outlined" onClick={() => fetchAudit()} disabled={auditLoading}>
                  Refresh
                </Button>
              </Stack>
            </Stack>

            {auditError ? <Alert severity="error">{auditError}</Alert> : null}
            {auditLoading ? <LinearProgress /> : null}

            <FormControlLabel
              control={<Checkbox checked={showAuditRaw} onChange={e => setShowAuditRaw(e.target.checked)} />}
              label="Show raw JSON"
            />

            {audit && showAuditRaw ? (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(audit)}</pre>
            ) : audit ? (
              <Typography variant="body2" color="text.secondary">
                Audit data loaded. Enable “Show raw JSON” to inspect.
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No audit data loaded yet.
              </Typography>
            )}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Debug request/response log
              </Typography>
              <Button variant="outlined" onClick={() => setDebugLogs([])}>
                Clear
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={debugVerbose} onChange={e => setDebugVerbose(e.target.checked)} />}
                label="Verbose (include JSON)"
              />
              <Typography variant="caption" color="text.secondary">
                {debugLogs.length} entries
              </Typography>
            </Stack>

            <Box
              ref={debugLogRef}
              sx={{
                maxHeight: 240,
                overflow: 'auto',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.default',
              }}
            >
              <Stack spacing={0.75}>
                {debugLogs.length ? (
                  debugLogs.map((l, idx) => (
                    <Box key={idx}>
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          color:
                            l.kind === 'ERR'
                              ? 'error.main'
                              : l.kind === 'REQ'
                                ? 'info.main'
                                : l.kind === 'RES'
                                  ? 'success.main'
                                  : 'text.secondary',
                        }}
                      >
                        [{new Date(l.ts).toLocaleTimeString()}] {l.kind} {l.message}
                      </Typography>
                      {debugVerbose && l.data !== undefined ? (
                        <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(l.data)}</pre>
                      ) : null}
                    </Box>
                  ))
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No requests yet.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );
};

const copyToClipboard = async (text: string): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  throw new Error('Clipboard API is not available in this environment');
};

const EasyflexSyncPage: React.FC = () => {
  const { accessToken } = useSelector((state: RootState) => (state as any).auth);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab: EasyflexTabKey = isEasyflexTabKey(searchParams.get('tab')) ? (searchParams.get('tab') as EasyflexTabKey) : 'dashboard';

  const [status, setStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const [importStatus, setImportStatus] = useState<string>('');
  const [importLocatie, setImportLocatie] = useState<string>('');
  const [importStartDate, setImportStartDate] = useState<string>('');
  const [importEndDate, setImportEndDate] = useState<string>('');
  const [startDateWarnOpen, setStartDateWarnOpen] = useState<boolean>(false);

  const [readEndpoint, setReadEndpoint] = useState<'reference-cache' | 'timecards'>('reference-cache');
  const [readOrganization, setReadOrganization] = useState<string>('');
  const [readFreshness, setReadFreshness] = useState<'cached' | 'refresh_async' | 'refresh_sync'>('cached');
  const [readMaxStaleSeconds, setReadMaxStaleSeconds] = useState<string>('');
  const [readBudgetMs, setReadBudgetMs] = useState<string>('2500');
  const [readReferenceKind, setReadReferenceKind] = useState<string>('inschrijver');
  const [readTimecardsWorkerRegistrationNumber, setReadTimecardsWorkerRegistrationNumber] = useState<string>('');

  const [workerId, setWorkerId] = useState<string>('');
  const [direction, setDirection] = useState<string>('EASYFLEX_TO_LOCAL');
  const [forceSync, setForceSync] = useState<boolean>(false);
  const [resolveConflicts, setResolveConflicts] = useState<boolean>(true);
  const [autoResolve, setAutoResolve] = useState<boolean>(true);

  const [bulkWorkerIds, setBulkWorkerIds] = useState<string>('');

  const [syncJobs, setSyncJobs] = useState<any[] | null>(null);
  const [syncJobsLoading, setSyncJobsLoading] = useState<boolean>(false);
  const [syncJobsError, setSyncJobsError] = useState<string | null>(null);
  const [syncJobsOrganizations, setSyncJobsOrganizations] = useState<string>('');
  const [syncJobsWait, setSyncJobsWait] = useState<boolean>(false);
  const [syncJobsWaitTimeoutSeconds, setSyncJobsWaitTimeoutSeconds] = useState<number>(900);

  // Live sync progress state
  const [activeSyncRunId, setActiveSyncRunId] = useState<string | null>(null);
  const [showLiveProgress, setShowLiveProgress] = useState<boolean>(true);
  const liveProgressRef = useRef<HTMLDivElement>(null);

  const [writeFreeze, setWriteFreeze] = useState<any | null>(null);
  const [writeFreezeLoading, setWriteFreezeLoading] = useState<boolean>(false);
  const [writeFreezeError, setWriteFreezeError] = useState<string | null>(null);
  const [writeFreezeReason, setWriteFreezeReason] = useState<string>('');
  const [writeFreezeSaving, setWriteFreezeSaving] = useState<boolean>(false);
  const [writeFreezeConfirmOpen, setWriteFreezeConfirmOpen] = useState<boolean>(false);
  const [writeFreezeTargetFrozen, setWriteFreezeTargetFrozen] = useState<boolean | null>(null);
  const [writeFreezeConfirmInput, setWriteFreezeConfirmInput] = useState<string>('');

  const [runtime, setRuntime] = useState<any | null>(null);
  const [runtimeLoading, setRuntimeLoading] = useState<boolean>(false);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);

  const [statsWindowHours, setStatsWindowHours] = useState<number>(24);
  const [stats, setStats] = useState<any | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [statsAutoRefresh, setStatsAutoRefresh] = useState<boolean>(true);

  const [recentFailedRuns, setRecentFailedRuns] = useState<any[] | null>(null);
  const [recentErrorLogs, setRecentErrorLogs] = useState<any[] | null>(null);
  const [recentErrorsLoading, setRecentErrorsLoading] = useState<boolean>(false);
  const [recentErrorsError, setRecentErrorsError] = useState<string | null>(null);

  const [syncIssues, setSyncIssues] = useState<any | null>(null);
  const [syncIssuesLoading, setSyncIssuesLoading] = useState<boolean>(false);
  const [syncIssuesError, setSyncIssuesError] = useState<string | null>(null);

  const [syncLogs, setSyncLogs] = useState<any[] | null>(null);
  const [syncLogsTotal, setSyncLogsTotal] = useState<number>(0);
  const [syncLogsLimit, setSyncLogsLimit] = useState<number>(50);
  const [syncLogsOffset, setSyncLogsOffset] = useState<number>(0);
  const [syncLogsWorkerId, setSyncLogsWorkerId] = useState<string>('');
  const [syncLogsEasyflexRegistrationNumber, setSyncLogsEasyflexRegistrationNumber] = useState<string>('');
  const [syncLogsSyncRunId, setSyncLogsSyncRunId] = useState<string>('');
  const [syncLogsOrganizationName, setSyncLogsOrganizationName] = useState<string>('');
  const [syncLogsEntity, setSyncLogsEntity] = useState<string>('');
  const [syncLogsOperation, setSyncLogsOperation] = useState<string>('');
  const [syncLogsResult, setSyncLogsResult] = useState<string>('');
  const [syncLogsInitiatedBy, setSyncLogsInitiatedBy] = useState<string>('');
  const [syncLogsQuery, setSyncLogsQuery] = useState<string>('');
  const [syncLogsSince, setSyncLogsSince] = useState<string>('');
  const [syncLogsUntil, setSyncLogsUntil] = useState<string>('');
  const [syncLogsLoading, setSyncLogsLoading] = useState<boolean>(false);
  const [syncLogsError, setSyncLogsError] = useState<string | null>(null);
  const [selectedSyncLog, setSelectedSyncLog] = useState<any | null>(null);
  const [syncLogsAutoRefresh, setSyncLogsAutoRefresh] = useState<boolean>(true);

  const [collisions, setCollisions] = useState<any[] | null>(null);
  const [collisionsTotal, setCollisionsTotal] = useState<number>(0);
  const [collisionsLimit, setCollisionsLimit] = useState<number>(50);
  const [collisionsOffset, setCollisionsOffset] = useState<number>(0);
  const [collisionsType, setCollisionsType] = useState<string>('');
  const [collisionsExternalId, setCollisionsExternalId] = useState<string>('');
  const [collisionsOrganization, setCollisionsOrganization] = useState<string>('');
  const [collisionsSince, setCollisionsSince] = useState<string>('');
  const [collisionsUntil, setCollisionsUntil] = useState<string>('');
  const [collisionsIncludeImpacted, setCollisionsIncludeImpacted] = useState<boolean>(true);
  const [collisionsLoading, setCollisionsLoading] = useState<boolean>(false);
  const [collisionsError, setCollisionsError] = useState<string | null>(null);
  const [selectedCollision, setSelectedCollision] = useState<any | null>(null);
  const [collisionsAutoRefresh, setCollisionsAutoRefresh] = useState<boolean>(true);

  const [syncRuns, setSyncRuns] = useState<any[] | null>(null);
  const [syncRunsTotal, setSyncRunsTotal] = useState<number>(0);
  const [syncRunsLimit, setSyncRunsLimit] = useState<number>(50);
  const [syncRunsOffset, setSyncRunsOffset] = useState<number>(0);
  const [syncRunsStatus, setSyncRunsStatus] = useState<string>('');
  const [syncRunsKey, setSyncRunsKey] = useState<string>('');
  const [syncRunsOrganization, setSyncRunsOrganization] = useState<string>('');
  const [syncRunsSince, setSyncRunsSince] = useState<string>('');
  const [syncRunsUntil, setSyncRunsUntil] = useState<string>('');
  const [syncRunsLoading, setSyncRunsLoading] = useState<boolean>(false);
  const [syncRunsError, setSyncRunsError] = useState<string | null>(null);
  const [selectedSyncRun, setSelectedSyncRun] = useState<any | null>(null);
  const [selectedSyncRunEvents, setSelectedSyncRunEvents] = useState<any | null>(null);
  const [selectedSyncRunDetailLoading, setSelectedSyncRunDetailLoading] = useState<boolean>(false);
  const [selectedSyncRunDetailError, setSelectedSyncRunDetailError] = useState<string | null>(null);
  const [syncRunsAutoRefresh, setSyncRunsAutoRefresh] = useState<boolean>(true);

  const optionsPayload = useMemo(
    () => ({
      direction,
      forceSync,
      resolveConflicts,
      autoResolve,
    }),
    [direction, forceSync, resolveConflicts, autoResolve],
  );

  // SSE hook for live sync progress
  const {
    events: liveEvents,
    connected: sseConnected,
    error: sseError,
    latestPhase,
    latestProgress,
    completed: syncCompleted,
    disconnect: disconnectSse,
  } = useSyncEventStream(activeSyncRunId, {
    accessToken,
    onComplete: () => {
      fetchSyncJobs();
      fetchSyncLogs({ offset: 0 });
    },
  });

  // Debug: log SSE state changes
  useEffect(() => {
    if (activeSyncRunId) {
      console.log('[EasyflexSync] SSE state:', {
        activeSyncRunId,
        hasToken: !!accessToken,
        tokenLength: accessToken?.length,
        sseConnected,
        sseError,
      });
    }
  }, [activeSyncRunId, accessToken, sseConnected, sseError]);

  // Auto-scroll live progress when new events arrive
  useEffect(() => {
    if (liveProgressRef.current && liveEvents.length > 0) {
      liveProgressRef.current.scrollTop = liveProgressRef.current.scrollHeight;
    }
  }, [liveEvents]);

  const run = async (fn: () => Promise<any>) => {
    setStatus('loading');
    setError(null);
    setResult(null);
    try {
      const data = await fn();
      setResult(data);
      setStatus('done');
    } catch (e: any) {
      setStatus('error');
      setError(e?.message || 'Request failed');
    }
  };

  const runImport = () => {
    run(async () => {
      const payload: any = {};
      if (importStatus) payload.status = importStatus;
      if (importLocatie) payload.locatie = Number(importLocatie);
      if (importStartDate) payload.startDate = importStartDate;
      if (importEndDate) payload.endDate = importEndDate;
      const res = await centralDbRequest({ method: 'POST', path: '/easyflex/import', token: accessToken, body: payload });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      return res.data;
    });
  };

  const pollJobBySyncRunId = async (syncRunId: string, options?: { timeoutSeconds?: number }) => {
    const timeoutSeconds = Math.max(5, Math.min(3600, Number(options?.timeoutSeconds ?? 900)));
    const startedAt = Date.now();

    while (true) {
      const res = await centralDbRequest({
        method: 'GET',
        path: `/easyflex/syncs/by-sync-run/${encodeURIComponent(syncRunId)}`,
        token: accessToken,
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

      const job = res.data?.job;
      const state = String(job?.state ?? '');
      if (state === 'completed') return job;
      if (state === 'failed') throw new Error(String(job?.failedReason ?? 'Job failed'));

      if (Date.now() - startedAt > timeoutSeconds * 1000) {
        throw new Error(`Timed out waiting for job (syncRunId=${syncRunId})`);
      }
      await new Promise(r => window.setTimeout(r, 2000));
    }
  };

  const runCacheInspectorFetch = (freshnessOverride?: 'cached' | 'refresh_async' | 'refresh_sync') =>
    run(async () => {
      const freshness = freshnessOverride ?? readFreshness;
      const query: any = { meta: '1', freshness };
      if (readOrganization.trim()) query.organization = readOrganization.trim();
      if (readMaxStaleSeconds.trim()) query.maxStaleSeconds = readMaxStaleSeconds.trim();

      if (readEndpoint === 'reference-cache') {
        query.kind = readReferenceKind;
        const res = await centralDbRequest({
          method: 'GET',
          path: '/easyflex/reference-cache',
          token: accessToken,
          query,
        });
        if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
        return res.data;
      }

      if (readEndpoint === 'timecards') {
        if (readTimecardsWorkerRegistrationNumber.trim()) {
          query.workerRegistrationNumber = readTimecardsWorkerRegistrationNumber.trim();
        }
        if (readBudgetMs.trim()) query.budgetMs = readBudgetMs.trim();
        const res = await centralDbRequest({
          method: 'GET',
          path: '/easyflex/timecards',
          token: accessToken,
          query,
        });
        if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
        return res.data;
      }

      throw new Error('Unknown endpoint');
    });

  const fetchSyncJobs = async () => {
    setSyncJobsLoading(true);
    setSyncJobsError(null);
    try {
      const res = await centralDbRequest({ method: 'GET', path: '/easyflex/syncs', token: accessToken });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setSyncJobs(res.data?.items ?? []);
    } catch (e: any) {
      setSyncJobsError(e?.message || 'Failed to load sync jobs');
      setSyncJobs(null);
    } finally {
      setSyncJobsLoading(false);
    }
  };

  const fetchWriteFreeze = async () => {
    setWriteFreezeLoading(true);
    setWriteFreezeError(null);
    try {
      const res = await centralDbRequest({ method: 'GET', path: '/admin/easyflex/write-freeze', token: accessToken });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setWriteFreeze(res.data ?? null);
    } catch (e: any) {
      setWriteFreezeError(e?.message || 'Failed to load write-freeze state');
      setWriteFreeze(null);
    } finally {
      setWriteFreezeLoading(false);
    }
  };

  const fetchRuntime = async () => {
    setRuntimeLoading(true);
    setRuntimeError(null);
    try {
      const res = await centralDbRequest({ method: 'GET', path: '/easyflex/runtime', token: accessToken });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setRuntime(res.data ?? null);
    } catch (e: any) {
      setRuntimeError(e?.message || 'Failed to load runtime diagnostics');
      setRuntime(null);
    } finally {
      setRuntimeLoading(false);
    }
  };

  const openWriteFreezeConfirm = (targetFrozen: boolean) => {
    setWriteFreezeTargetFrozen(targetFrozen);
    setWriteFreezeConfirmInput('');
    setWriteFreezeConfirmOpen(true);
  };

  const confirmWriteFreeze = async () => {
    if (!writeFreeze) return;
    if (writeFreeze?.envForced) return;
    if (writeFreezeTargetFrozen === null) return;

    setWriteFreezeSaving(true);
    setWriteFreezeError(null);
    try {
      const res = await centralDbRequest({
        method: 'PUT',
        path: '/admin/easyflex/write-freeze',
        token: accessToken,
        body: {
          frozen: writeFreezeTargetFrozen,
          ...(writeFreezeReason.trim() ? { reason: writeFreezeReason.trim() } : {}),
        },
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setWriteFreeze(res.data ?? null);
      setWriteFreezeReason('');
      setWriteFreezeConfirmOpen(false);
      setWriteFreezeTargetFrozen(null);
      setWriteFreezeConfirmInput('');
    } catch (e: any) {
      setWriteFreezeError(e?.message || 'Failed to update write-freeze state');
    } finally {
      setWriteFreezeSaving(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    setStatsError(null);
    try {
      const res = await centralDbRequest({
        method: 'GET',
        path: '/admin/easyflex/stats',
        token: accessToken,
        query: { windowHours: String(statsWindowHours) },
      });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setStats(res.data ?? null);
    } catch (e: any) {
      setStatsError(e?.message || 'Failed to load stats');
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchRecentErrors = async () => {
    setRecentErrorsLoading(true);
    setRecentErrorsError(null);
    try {
      const [runsRes, logsRes] = await Promise.all([
        centralDbRequest({
          method: 'GET',
          path: '/easyflex/sync-runs',
          token: accessToken,
          query: { status: 'FAILED', limit: '10', offset: '0' },
        }),
        centralDbRequest({
          method: 'GET',
          path: '/easyflex/sync-logs',
          token: accessToken,
          query: { result: 'FAILED', limit: '20', offset: '0' },
        }),
      ]);
      if (!runsRes.ok) throw new Error(typeof runsRes.data === 'string' ? runsRes.data : `HTTP ${runsRes.status}`);
      if (!logsRes.ok) throw new Error(typeof logsRes.data === 'string' ? logsRes.data : `HTTP ${logsRes.status}`);

      setRecentFailedRuns(Array.isArray(runsRes.data?.items) ? runsRes.data.items : []);
      setRecentErrorLogs(Array.isArray(logsRes.data?.items) ? logsRes.data.items : []);
    } catch (e: any) {
      setRecentErrorsError(e?.message || 'Failed to load recent errors');
      setRecentFailedRuns(null);
      setRecentErrorLogs(null);
    } finally {
      setRecentErrorsLoading(false);
    }
  };

  const fetchSyncIssues = async () => {
    setSyncIssuesLoading(true);
    setSyncIssuesError(null);
    try {
      const res = await centralDbRequest({ method: 'GET', path: '/easyflex/sync-issues', token: accessToken });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
      setSyncIssues(res.data ?? null);
    } catch (e: any) {
      setSyncIssuesError(e?.message || 'Failed to load sync issues');
      setSyncIssues(null);
    } finally {
      setSyncIssuesLoading(false);
    }
  };

  const fetchSyncLogs = async (
    override?: Partial<{ limit: number; offset: number }> & {
      queryOverride?: Partial<{
        workerId: string;
        easyflexRegistrationNumber: string;
        syncRunId: string;
        organizationName: string;
        entity: string;
        operation: string;
        result: string;
        initiatedBy: string;
        q: string;
        since: string;
        until: string;
      }>;
    },
  ) => {
    setSyncLogsLoading(true);
    setSyncLogsError(null);
    try {
      const qOverride = override?.queryOverride ?? {};

      const query: any = {
        limit: override?.limit ?? syncLogsLimit,
        offset: override?.offset ?? syncLogsOffset,
      };

      const workerId = qOverride.workerId !== undefined ? qOverride.workerId : syncLogsWorkerId;
      const easyflexRegistrationNumber =
        qOverride.easyflexRegistrationNumber !== undefined ? qOverride.easyflexRegistrationNumber : syncLogsEasyflexRegistrationNumber;
      const syncRunId = qOverride.syncRunId !== undefined ? qOverride.syncRunId : syncLogsSyncRunId;
      const organizationName = qOverride.organizationName !== undefined ? qOverride.organizationName : syncLogsOrganizationName;
      const entity = qOverride.entity !== undefined ? qOverride.entity : syncLogsEntity;
      const operation = qOverride.operation !== undefined ? qOverride.operation : syncLogsOperation;
      const result = qOverride.result !== undefined ? qOverride.result : syncLogsResult;
      const initiatedBy = qOverride.initiatedBy !== undefined ? qOverride.initiatedBy : syncLogsInitiatedBy;
      const q = qOverride.q !== undefined ? qOverride.q : syncLogsQuery;
      const since = qOverride.since !== undefined ? qOverride.since : syncLogsSince;
      const until = qOverride.until !== undefined ? qOverride.until : syncLogsUntil;

      if (String(workerId ?? '').trim()) query.workerId = String(workerId).trim();
      if (String(easyflexRegistrationNumber ?? '').trim()) query.easyflexRegistrationNumber = String(easyflexRegistrationNumber).trim();
      if (String(syncRunId ?? '').trim()) query.syncRunId = String(syncRunId).trim();
      if (String(organizationName ?? '').trim()) query.organizationName = String(organizationName).trim();
      if (String(entity ?? '').trim()) query.entity = String(entity).trim();
      if (String(operation ?? '').trim()) query.operation = String(operation).trim();
      if (String(result ?? '').trim()) query.result = String(result).trim();
      if (String(initiatedBy ?? '').trim()) query.initiatedBy = String(initiatedBy).trim();
      if (String(q ?? '').trim()) query.q = String(q).trim();
      if (String(since ?? '').trim()) query.since = String(since).trim();
      if (String(until ?? '').trim()) query.until = String(until).trim();

      const res = await centralDbRequest({ method: 'GET', path: '/easyflex/sync-logs', token: accessToken, query });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

      setSyncLogs(Array.isArray(res.data?.items) ? res.data.items : []);
      setSyncLogsTotal(Number(res.data?.total ?? 0));
      setSyncLogsLimit(Number(res.data?.limit ?? query.limit ?? 50));
      setSyncLogsOffset(Number(res.data?.offset ?? query.offset ?? 0));
    } catch (e: any) {
      setSyncLogsError(e?.message || 'Failed to load sync logs');
      setSyncLogs(null);
      setSyncLogsTotal(0);
    } finally {
      setSyncLogsLoading(false);
    }
  };

  const openLogsTab = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', 'logs');
      return next;
    });
  };

  const openLogsWithFilters = (filters: {
    initiatedBy?: string;
    operation?: string;
    result?: string;
    q?: string;
    entity?: string;
  }) => {
    openLogsTab();
    setSyncLogsOffset(0);
    setSelectedSyncLog(null);

    setSyncLogsInitiatedBy(filters.initiatedBy ?? '');
    setSyncLogsOperation(filters.operation ?? '');
    setSyncLogsResult(filters.result ?? '');
    setSyncLogsQuery(filters.q ?? '');
    setSyncLogsEntity(filters.entity ?? '');

    fetchSyncLogs({ offset: 0, queryOverride: filters as any });
  };

  const fetchCollisions = async (override?: Partial<{ limit: number; offset: number }>) => {
    setCollisionsLoading(true);
    setCollisionsError(null);
    try {
      const query: any = {
        limit: override?.limit ?? collisionsLimit,
        offset: override?.offset ?? collisionsOffset,
      };
      if (collisionsType) query.collisionType = collisionsType;
      if (collisionsExternalId.trim()) query.externalId = collisionsExternalId.trim();
      if (collisionsOrganization.trim()) query.organization = collisionsOrganization.trim();
      if (collisionsSince.trim()) query.since = collisionsSince.trim();
      if (collisionsUntil.trim()) query.until = collisionsUntil.trim();
      if (collisionsIncludeImpacted) query.includeImpacted = '1';

      const res = await centralDbRequest({ method: 'GET', path: '/admin/easyflex/collisions', token: accessToken, query });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

      setCollisions(Array.isArray(res.data?.items) ? res.data.items : []);
      setCollisionsTotal(Number(res.data?.total ?? 0));
      setCollisionsLimit(Number(res.data?.limit ?? query.limit ?? 50));
      setCollisionsOffset(Number(res.data?.offset ?? query.offset ?? 0));
    } catch (e: any) {
      setCollisionsError(e?.message || 'Failed to load collisions');
      setCollisions(null);
      setCollisionsTotal(0);
    } finally {
      setCollisionsLoading(false);
    }
  };

  const fetchSyncRuns = async (override?: Partial<{ limit: number; offset: number }>) => {
    setSyncRunsLoading(true);
    setSyncRunsError(null);
    try {
      const query: any = {
        limit: override?.limit ?? syncRunsLimit,
        offset: override?.offset ?? syncRunsOffset,
      };
      if (syncRunsStatus) query.status = syncRunsStatus;
      if (syncRunsKey.trim()) query.key = syncRunsKey.trim();
      if (syncRunsOrganization.trim()) query.organization = syncRunsOrganization.trim();
      if (syncRunsSince.trim()) query.since = syncRunsSince.trim();
      if (syncRunsUntil.trim()) query.until = syncRunsUntil.trim();

      const res = await centralDbRequest({ method: 'GET', path: '/easyflex/sync-runs', token: accessToken, query });
      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

      setSyncRuns(Array.isArray(res.data?.items) ? res.data.items : []);
      setSyncRunsTotal(Number(res.data?.total ?? 0));
      setSyncRunsLimit(Number(res.data?.limit ?? query.limit ?? 50));
      setSyncRunsOffset(Number(res.data?.offset ?? query.offset ?? 0));
    } catch (e: any) {
      setSyncRunsError(e?.message || 'Failed to load sync runs');
      setSyncRuns(null);
      setSyncRunsTotal(0);
    } finally {
      setSyncRunsLoading(false);
    }
  };

  const fetchSyncRunEvents = async (syncRunId: string) => {
    const id = String(syncRunId ?? '').trim();
    if (!id) return;
    const res = await centralDbRequest({
      method: 'GET',
      path: `/easyflex/sync-runs/${encodeURIComponent(id)}/events`,
      token: accessToken,
      query: { limit: 100, offset: 0 },
    });
    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
    setSelectedSyncRunEvents(res.data ?? null);
  };

  const fetchSyncRunDetail = async (syncRunId: string) => {
    const id = String(syncRunId ?? '').trim();
    if (!id) return;
    setSelectedSyncRunDetailLoading(true);
    setSelectedSyncRunDetailError(null);
    setSelectedSyncRun(null);
    setSelectedSyncRunEvents(null);
    try {
      const [runRes, eventsRes] = await Promise.all([
        centralDbRequest({ method: 'GET', path: `/easyflex/sync-runs/${encodeURIComponent(id)}`, token: accessToken }),
        centralDbRequest({
          method: 'GET',
          path: `/easyflex/sync-runs/${encodeURIComponent(id)}/events`,
          token: accessToken,
          query: { limit: 100, offset: 0 },
        }),
      ]);
      if (!runRes.ok) throw new Error(typeof runRes.data === 'string' ? runRes.data : `HTTP ${runRes.status}`);
      if (!eventsRes.ok) throw new Error(typeof eventsRes.data === 'string' ? eventsRes.data : `HTTP ${eventsRes.status}`);
      setSelectedSyncRun(runRes.data ?? null);
      setSelectedSyncRunEvents(eventsRes.data ?? null);
    } catch (e: any) {
      setSelectedSyncRunDetailError(e?.message || 'Failed to load sync run detail');
      setSelectedSyncRun(null);
      setSelectedSyncRunEvents(null);
    } finally {
      setSelectedSyncRunDetailLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncJobs();
    fetchWriteFreeze();
    fetchRuntime();
    fetchStats();
    fetchRecentErrors();
    fetchSyncIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    fetchSyncLogs({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    fetchCollisions({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    fetchSyncRuns({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  useEffect(() => {
    if (activeTab !== 'logs') return;
    const syncRunId = String(searchParams.get('syncRunId') ?? '').trim();
    if (!syncRunId) return;
    if (syncRunId === syncLogsSyncRunId) return;
    setSyncLogsSyncRunId(syncRunId);
    setSyncLogsOffset(0);
    setSelectedSyncLog(null);
    fetchSyncLogs({ offset: 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchParams.toString()]);

  useEffect(() => {
    const anyRunning = Array.isArray(syncJobs) && syncJobs.some(j => j?.running);
    if (activeTab !== 'dashboard' && !anyRunning) return;
    if (!statsAutoRefresh && !anyRunning) return;
    const t = window.setInterval(() => fetchStats(), 30000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsAutoRefresh, syncJobs, statsWindowHours]);

  useEffect(() => {
    const anyRunning = Array.isArray(syncJobs) && syncJobs.some(j => j?.running);
    if (!anyRunning) return;
    const t = window.setInterval(() => fetchSyncJobs(), 5000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncJobs]);

  useEffect(() => {
    const anyRunning = Array.isArray(syncJobs) && syncJobs.some(j => j?.running);
    if (activeTab !== 'logs' && !anyRunning) return;
    if (!syncLogsAutoRefresh && !anyRunning) return;
    const t = window.setInterval(() => fetchSyncLogs(), 10000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    syncLogsAutoRefresh,
    syncJobs,
    syncLogsLimit,
    syncLogsOffset,
    syncLogsWorkerId,
    syncLogsEasyflexRegistrationNumber,
    syncLogsSyncRunId,
    syncLogsOrganizationName,
    syncLogsEntity,
    syncLogsOperation,
    syncLogsResult,
    syncLogsInitiatedBy,
    syncLogsQuery,
    syncLogsSince,
    syncLogsUntil,
  ]);

  useEffect(() => {
    const anyRunning = Array.isArray(syncJobs) && syncJobs.some(j => j?.running);
    if (activeTab !== 'collisions' && !anyRunning) return;
    if (!collisionsAutoRefresh && !anyRunning) return;
    const t = window.setInterval(() => fetchCollisions(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    collisionsAutoRefresh,
    syncJobs,
    collisionsLimit,
    collisionsOffset,
    collisionsType,
    collisionsExternalId,
    collisionsOrganization,
    collisionsSince,
    collisionsUntil,
    collisionsIncludeImpacted,
  ]);

  useEffect(() => {
    const anyRunning = Array.isArray(syncJobs) && syncJobs.some(j => j?.running);
    if (activeTab !== 'logs' && !anyRunning) return;
    if (!syncRunsAutoRefresh && !anyRunning) return;
    const t = window.setInterval(() => fetchSyncRuns(), 15000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    syncRunsAutoRefresh,
    syncJobs,
    syncRunsLimit,
    syncRunsOffset,
    syncRunsStatus,
    syncRunsKey,
    syncRunsOrganization,
    syncRunsSince,
    syncRunsUntil,
  ]);

  return (
    <Stack spacing={2}>
      <Typography variant="h5" fontWeight={800}>
        Easyflex Sync
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, next: EasyflexTabKey) => {
          const p = new URLSearchParams(searchParams);
          p.set('tab', next);
          setSearchParams(p, { replace: true });
        }}
        variant="scrollable"
        allowScrollButtonsMobile
        aria-label="Easyflex operator tabs"
      >
        <Tab label="Dashboard" value="dashboard" {...a11yTabProps('dashboard')} />
        <Tab label="Jobs" value="jobs" {...a11yTabProps('jobs')} />
        <Tab label="Logs" value="logs" {...a11yTabProps('logs')} />
        <Tab label="Cache" value="cache" {...a11yTabProps('cache')} />
        <Tab label="Collisions" value="collisions" {...a11yTabProps('collisions')} />
        <Tab label="Freeze" value="freeze" {...a11yTabProps('freeze')} />
        <Tab label="Tools" value="tools" {...a11yTabProps('tools')} />
      </Tabs>

      <Dialog open={writeFreezeConfirmOpen} onClose={() => setWriteFreezeConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm {writeFreezeTargetFrozen ? 'Freeze' : 'Unfreeze'}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            {writeFreeze?.envForced ? (
              <Alert severity="warning">
                Writes are forced frozen by env (`EASYFLEX_WRITES_FROZEN=true`). UI toggles cannot unfreeze until the env override is removed.
              </Alert>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              This changes the global write-freeze state in Central DB (DB-backed).
            </Typography>
            <Typography variant="body2">
              Current: <strong>{writeFreeze?.frozen ? 'FROZEN' : 'UNFROZEN'}</strong>
            </Typography>
            <Typography variant="body2">
              Target: <strong>{writeFreezeTargetFrozen ? 'FROZEN' : 'UNFROZEN'}</strong>
            </Typography>
            <TextField
              label={`Type ${writeFreezeTargetFrozen ? 'FREEZE' : 'UNFREEZE'} to confirm`}
              value={writeFreezeConfirmInput}
              onChange={e => setWriteFreezeConfirmInput(e.target.value)}
              fullWidth
              disabled={writeFreezeSaving || Boolean(writeFreeze?.envForced)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteFreezeConfirmOpen(false)} disabled={writeFreezeSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={writeFreezeTargetFrozen ? 'error' : 'success'}
            onClick={confirmWriteFreeze}
            disabled={
              writeFreezeSaving ||
              Boolean(writeFreeze?.envForced) ||
              (writeFreezeTargetFrozen ? writeFreezeConfirmInput !== 'FREEZE' : writeFreezeConfirmInput !== 'UNFREEZE')
            }
          >
            {writeFreezeSaving ? <CircularProgress size={18} /> : writeFreezeTargetFrozen ? 'Freeze' : 'Unfreeze'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={startDateWarnOpen} onClose={() => setStartDateWarnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Warning: Start date far in the past</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 1 }}>
            The start date <strong>{importStartDate}</strong> is more than 2 weeks before today. Are you sure you want to proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartDateWarnOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => { setStartDateWarnOpen(false); runImport(); }}>
            Proceed anyway
          </Button>
        </DialogActions>
      </Dialog>

      {activeTab === 'freeze' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Write freeze
                </Typography>
                <Button variant="outlined" onClick={() => fetchWriteFreeze()} disabled={writeFreezeLoading}>
                  Refresh
                </Button>
              </Stack>

            {writeFreezeError ? <Alert severity="error">{writeFreezeError}</Alert> : null}
            {writeFreezeLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ flex: 1 }}>
                <Chip
                  label={writeFreeze?.frozen ? 'FROZEN' : 'UNFROZEN'}
                  color={writeFreeze?.frozen ? 'error' : 'success'}
                  variant="filled"
                />
                {writeFreeze?.envForced ? <Chip label="env forced" color="warning" variant="outlined" /> : null}
                <Typography variant="body2" color="text.secondary">
                  {writeFreeze?.updatedAt ? `last toggle: ${writeFreeze.updatedAt}` : 'no toggles recorded yet'}
                  {writeFreeze?.updatedBy ? ` (${writeFreeze.updatedBy})` : ''}
                </Typography>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { xs: '100%', md: 520 } }}>
                <TextField
                  label="Reason (optional)"
                  value={writeFreezeReason}
                  onChange={e => setWriteFreezeReason(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={writeFreezeLoading || writeFreezeSaving}
                />
                <Button
                  variant="contained"
                  color={writeFreeze?.frozen ? 'success' : 'error'}
                  onClick={() => openWriteFreezeConfirm(!Boolean(writeFreeze?.frozen))}
                  disabled={writeFreezeLoading || writeFreezeSaving || !writeFreeze || Boolean(writeFreeze?.envForced)}
                >
                  {writeFreeze?.frozen ? 'Unfreeze' : 'Freeze'}
                </Button>
              </Stack>
            </Stack>

            {writeFreeze?.reason ? (
              <Typography variant="body2" color="text.secondary">
                reason: {String(writeFreeze.reason)}
              </Typography>
            ) : null}
            {writeFreeze?.envForced ? (
              <Alert severity="warning">
                Writes are forced frozen by env (`EASYFLEX_WRITES_FROZEN=true`). UI toggles cannot unfreeze until the env override is removed.
              </Alert>
            ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'dashboard' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Runtime (org mapping)
                </Typography>
                <Button variant="outlined" onClick={() => fetchRuntime()} disabled={runtimeLoading}>
                  Refresh
                </Button>
              </Stack>

              {runtimeError ? <Alert severity="error">{runtimeError}</Alert> : null}
              {runtimeLoading ? <LinearProgress /> : null}

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} flexWrap="wrap" useFlexGap>
                <Chip size="small" variant="outlined" label={`connector=${String(runtime?.connector?.url ?? '—')}`} />
                <Chip
                  size="small"
                  variant="outlined"
                  color={runtime?.connector?.authDisabled ? 'warning' : 'default'}
                  label={`authDisabled=${String(Boolean(runtime?.connector?.authDisabled))}`}
                />
                <Chip
                  size="small"
                  variant="outlined"
                  color={runtime?.defaults?.defaultOrganizationValid === false ? 'error' : 'default'}
                  label={`defaultOrg=${String(runtime?.defaults?.defaultOrganization ?? '—')}`}
                />
                {runtime?.defaults?.defaultOrganizationValid !== undefined ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    color={runtime?.defaults?.defaultOrganizationValid ? 'success' : 'error'}
                    label={`defaultOrgValid=${String(Boolean(runtime?.defaults?.defaultOrganizationValid))}`}
                  />
                ) : null}
              </Stack>

              <Divider />

              <Typography variant="subtitle2" fontWeight={700}>
                Canonical organizations
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Array.isArray(runtime?.defaults?.canonicalOrganizations) && runtime.defaults.canonicalOrganizations.length ? (
                  runtime.defaults.canonicalOrganizations.map((o: any) => <Chip key={String(o)} size="small" label={String(o)} />)
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Stack>

              <Typography variant="subtitle2" fontWeight={700}>
                Connector organizations
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {Array.isArray(runtime?.organizations?.items) && runtime.organizations.items.length ? (
                  runtime.organizations.items.map((o: any) => (
                    <Chip key={String(o)} size="small" variant="outlined" label={String(o)} />
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                )}
              </Stack>

              <Divider />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Organization aliases (canonical → connector)
                </Typography>
                <Tooltip
                  title="Copy aliases JSON"
                  children={
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copyToClipboard(jsonStringify(runtime?.defaults?.organizationAliases ?? {}))}
                        disabled={!runtime}
                      >
                        Copy
                      </Button>
                    </span>
                  }
                />
              </Stack>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {jsonStringify(runtime?.defaults?.organizationAliases ?? {})}
              </pre>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Reverse aliases (connector → canonical[])
                </Typography>
                <Tooltip
                  title="Copy reverse mapping JSON"
                  children={
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copyToClipboard(jsonStringify(runtime?.defaults?.reverseOrganizationAliases ?? {}))}
                        disabled={!runtime}
                      >
                        Copy
                      </Button>
                    </span>
                  }
                />
              </Stack>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {jsonStringify(runtime?.defaults?.reverseOrganizationAliases ?? {})}
              </pre>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'dashboard' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Stats
                </Typography>
                <Button variant="outlined" onClick={() => fetchStats()} disabled={statsLoading}>
                  Refresh
                </Button>
              </Stack>

            {statsError ? <Alert severity="error">{statsError}</Alert> : null}
            {statsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                select
                label="window"
                value={String(statsWindowHours)}
                onChange={e => setStatsWindowHours(Number(e.target.value))}
                sx={{ width: { md: 220 } }}
              >
                <MenuItem value="1">Last 1h</MenuItem>
                <MenuItem value="24">Last 24h</MenuItem>
                <MenuItem value="168">Last 7d</MenuItem>
              </TextField>
              <Chip
                label={
                  stats?.range?.since && stats?.range?.until
                    ? `${String(stats.range.since)} → ${String(stats.range.until)}`
                    : '—'
                }
                variant="outlined"
              />
              <Chip
                label={`logs=${Number(stats?.totals?.logs ?? 0)} errors=${Number(stats?.totals?.errors ?? 0)} collisions=${Number(
                  stats?.totals?.collisions ?? 0,
                )}`}
                variant="outlined"
              />
              <FormControlLabel
                control={<Checkbox checked={statsAutoRefresh} onChange={e => setStatsAutoRefresh(e.target.checked)} />}
                label="Auto-refresh stats"
              />
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Inventory signals
            </Typography>
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`external-ingress attempts=${Number(stats?.inventory?.externalIngress?.attempts ?? 0)} errors=${Number(
                    stats?.inventory?.externalIngress?.errors ?? 0,
                  )}`}
                />
                {stats?.inventory?.externalIngress?.lastAt ? (
                  <Chip size="small" variant="outlined" label={`last=${String(stats.inventory.externalIngress.lastAt)}`} />
                ) : null}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    openLogsWithFilters({
                      initiatedBy: 'external-ingress',
                      operation: 'BULK_IMPORT',
                    })
                  }
                >
                  View ingress logs
                </Button>
              </Stack>

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`webhook-disabled attempts=${Number(stats?.inventory?.webhookDisabled?.attempts ?? 0)}`}
                />
                {stats?.inventory?.webhookDisabled?.lastAt ? (
                  <Chip size="small" variant="outlined" label={`last=${String(stats.inventory.webhookDisabled.lastAt)}`} />
                ) : null}
                <Button size="small" variant="outlined" onClick={() => openLogsWithFilters({ q: 'EASYFLEX_WEBHOOKS_DISABLED' })}>
                  View webhook logs
                </Button>
              </Stack>
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Per organization
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(stats?.byOrganization) && stats.byOrganization.length ? (
                stats.byOrganization.slice(0, 12).map((row: any, idx: number) => {
                  const key = String(row?.key ?? idx);
                  const label = key || '(default)';
                  return (
                    <Stack key={key || idx} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                      <Chip size="small" label={label} />
                      <Chip size="small" variant="outlined" label={`logs=${Number(row?.logs ?? 0)}`} />
                      <Chip size="small" variant="outlined" label={`errors=${Number(row?.errors ?? 0)}`} color={row?.errors ? 'error' : 'default'} />
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`collisions=${Number(row?.collisions ?? 0)}`}
                        color={row?.collisions ? 'warning' : 'default'}
                      />
                    </Stack>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No stats rows for this window.
                </Typography>
              )}
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Per entity
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(stats?.byEntity) && stats.byEntity.length ? (
                stats.byEntity.slice(0, 12).map((row: any, idx: number) => {
                  const key = String(row?.key ?? idx);
                  const label = key || '(unknown)';
                  return (
                    <Stack key={key || idx} direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                      <Chip size="small" label={label} />
                      <Chip size="small" variant="outlined" label={`logs=${Number(row?.logs ?? 0)}`} />
                      <Chip size="small" variant="outlined" label={`errors=${Number(row?.errors ?? 0)}`} color={row?.errors ? 'error' : 'default'} />
                    </Stack>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No stats rows for this window.
                </Typography>
              )}
            </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'dashboard' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Recent errors
                </Typography>
                <Button variant="outlined" onClick={() => fetchRecentErrors()} disabled={recentErrorsLoading}>
                  Refresh
                </Button>
              </Stack>

            {recentErrorsError ? <Alert severity="error">{recentErrorsError}</Alert> : null}
            {recentErrorsLoading ? <LinearProgress /> : null}

            <Typography variant="subtitle2" fontWeight={700}>
              Failed runs
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(recentFailedRuns) && recentFailedRuns.length ? (
                recentFailedRuns.map((r: any, idx: number) => {
                  const id = String(r?.syncRunId ?? idx);
                  const createdAt = r?.createdAt ? new Date(r.createdAt).toLocaleString() : '—';
                  const key = r?.key ? String(r.key) : '';
                  const orgs = Array.isArray(r?.organizations) ? r.organizations.join(', ') : '';
                  const errorMessage = r?.errorMessage ? String(r.errorMessage) : '';
                  return (
                    <Card key={id} variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {createdAt}
                            </Typography>
                            <Chip size="small" color="error" label="FAILED" />
                            {key ? <Chip size="small" variant="outlined" label={key} /> : null}
                            {orgs ? <Chip size="small" variant="outlined" label={orgs} /> : null}
                            <Chip size="small" variant="outlined" label={`syncRunId=${id}`} />
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={async () => {
                                setSelectedSyncRun(r);
                                setSelectedSyncRunEvents(null);
                                await fetchSyncRunEvents(id);
                              }}
                            >
                              Events
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSyncLogsSyncRunId(id);
                                setSyncLogsOffset(0);
                                setSelectedSyncLog(null);
                                fetchSyncLogs({ offset: 0 });
                              }}
                            >
                              Filter logs
                            </Button>
                            {key ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() =>
                                  run(async () => {
                                    const organizations = Array.isArray(r?.organizations)
                                      ? r.organizations.map((s: any) => String(s)).filter(Boolean)
                                      : [];
                                    const res = await centralDbRequest({
                                      method: 'POST',
                                      path: `/easyflex/syncs/${encodeURIComponent(key)}/start`,
                                      token: accessToken,
                                      body: organizations.length ? { organizations } : {},
                                    });
                                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);

                                    const syncRunId = String(res.data?.syncRunId ?? '').trim();
                                    if (syncRunId) {
                                      const p = new URLSearchParams(searchParams);
                                      p.set('tab', 'logs');
                                      p.set('syncRunId', syncRunId);
                                      setSearchParams(p, { replace: true });
                                      setSyncLogsSyncRunId(syncRunId);
                                      setSyncLogsOffset(0);
                                      setSelectedSyncLog(null);
                                      fetchSyncLogs({ offset: 0 });
                                    }

                                    await Promise.all([fetchSyncJobs(), fetchStats(), fetchRecentErrors()]);
                                    return res.data;
                                  })
                                }
                                disabled={status === 'loading'}
                              >
                                Rerun
                              </Button>
                            ) : null}
                          </Stack>
                          {errorMessage ? (
                            <Typography variant="body2" color="error">
                              {errorMessage}
                            </Typography>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No failed runs found (limit 10).
                </Typography>
              )}
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Error logs
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(recentErrorLogs) && recentErrorLogs.length ? (
                recentErrorLogs.map((l: any, idx: number) => {
                  const id = String(l?.id ?? idx);
                  const createdAt = l?.createdAt ? new Date(l.createdAt).toLocaleString() : '—';
                  const org = l?.organizationName ? String(l.organizationName) : '';
                  const entity = l?.entity ? String(l.entity) : '';
                  const op = l?.operation ? String(l.operation) : '';
                  const syncRunId = l?.syncRunId ? String(l.syncRunId) : '';
                  const res = l?.result ? String(l.result) : '';
                  return (
                    <Card key={id} variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {createdAt}
                            </Typography>
                            <Chip size="small" color="error" label={res || 'FAILED'} />
                            {org ? <Chip size="small" variant="outlined" label={org} /> : null}
                            {entity ? <Chip size="small" variant="outlined" label={entity} /> : null}
                            {op ? <Chip size="small" variant="outlined" label={op} /> : null}
                            {syncRunId ? <Chip size="small" variant="outlined" label={`syncRunId=${syncRunId}`} /> : null}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSelectedSyncLog(l);
                              }}
                            >
                              Details
                            </Button>
                            {syncRunId ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSyncLogsSyncRunId(syncRunId);
                                  setSyncLogsOffset(0);
                                  setSelectedSyncLog(null);
                                  fetchSyncLogs({ offset: 0 });
                                }}
                              >
                                Filter logs
                              </Button>
                            ) : null}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No error logs found (limit 20).
                </Typography>
              )}
            </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'jobs' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Data sync jobs
                </Typography>
                <Button variant="outlined" onClick={() => fetchSyncJobs()} disabled={syncJobsLoading}>
                  Refresh
                </Button>
              </Stack>

            {syncJobsError ? <Alert severity="error">{syncJobsError}</Alert> : null}
            {syncJobsLoading ? <LinearProgress /> : null}

            <TextField
              label="Organizations override (comma-separated; optional)"
              helperText="If set, sent to /easyflex/syncs/:key/start as { organizations: [...] } to limit which orgs run. See EASYFLEX_DATA_SYNC_ORGANIZATIONS / multi-business naming."
              value={syncJobsOrganizations}
              onChange={e => setSyncJobsOrganizations(e.target.value)}
              fullWidth
            />

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={syncJobsWait} onChange={e => setSyncJobsWait(e.target.checked)} />}
                label="Wait for completion (poll by syncRunId)"
              />
              <TextField
                label="Wait timeout (seconds)"
                value={String(syncJobsWaitTimeoutSeconds)}
                onChange={e => setSyncJobsWaitTimeoutSeconds(Math.max(5, Math.min(3600, Number(e.target.value || 900))))}
                sx={{ width: { md: 220 } }}
              />
            </Stack>

            {Array.isArray(syncJobs) ? (
              <Stack spacing={1}>
                {syncJobs.map(job => {
                  const key = String(job?.key || '');
                  const label = String(job?.label || key);
                  const envEveryMinutesKey = String(job?.envEveryMinutesKey || '');
                  const scheduledEveryMinutes = job?.scheduledEveryMinutes as number | undefined;
                  const running = !!job?.running;
                  const progress = job?.progress;
                  const progressNumber = typeof progress === 'number' ? progress : undefined;
                  const lastCompleted = job?.lastCompleted?.finishedOn ? new Date(job.lastCompleted.finishedOn).toLocaleString() : null;
                  const lastFailed = job?.lastFailed?.finishedOn ? new Date(job.lastFailed.finishedOn).toLocaleString() : null;
                  const lastCompletedAgeMinutes = job?.lastCompleted?.finishedOn
                    ? Math.max(0, Math.floor((Date.now() - Number(job.lastCompleted.finishedOn)) / 60000))
                    : undefined;
                  const staleBySchedule =
                    scheduledEveryMinutes && lastCompletedAgeMinutes !== undefined
                      ? lastCompletedAgeMinutes > Math.max(10, scheduledEveryMinutes * 2)
                      : false;

                  return (
                    <Card key={key} variant="outlined" sx={{ borderStyle: running ? 'solid' : 'dashed' }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                            <Stack spacing={0.25}>
                              <Typography fontWeight={800}>{label}</Typography>
                              <Typography variant="body2" color="text.secondary">
                                {job?.description || ''}
                              </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} alignItems="center">
                              {scheduledEveryMinutes ? (
                                <Chip size="small" label={`Scheduled every ${scheduledEveryMinutes}m`} variant="outlined" />
                              ) : (
                                <Chip size="small" label="Not scheduled" variant="outlined" />
                              )}
                              <Chip
                                size="small"
                                color={running ? 'warning' : 'default'}
                                label={running ? 'Running' : 'Idle'}
                                variant={running ? 'filled' : 'outlined'}
                              />
                              {staleBySchedule ? (
                                <Tooltip
                                  title={`Last completed ~${lastCompletedAgeMinutes}m ago; schedule is ${scheduledEveryMinutes}m`}
                                  children={<Chip size="small" color="warning" label="Stale" variant="filled" />}
                                />
                              ) : null}
                              <Tooltip title={running ? 'Already running' : 'Start sync'} children={
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={running || syncJobsLoading}
                                    onClick={() =>
                                      run(async () => {
                                        const orgs = syncJobsOrganizations
                                          .split(',')
                                          .map(s => s.trim())
                                          .filter(Boolean);
                                        const res = await centralDbRequest({
                                          method: 'POST',
                                          path: `/easyflex/syncs/${encodeURIComponent(key)}/start`,
                                          token: accessToken,
                                          body: orgs.length ? { organizations: orgs } : {},
                                        });
                                        if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                                        const syncRunId = String(res.data?.syncRunId ?? '').trim();

                                        // Connect SSE for live progress
                                        if (syncRunId) {
                                          console.log('[EasyflexSync] Starting SSE for syncRunId:', syncRunId);
                                          setActiveSyncRunId(syncRunId);
                                        } else {
                                          console.warn('[EasyflexSync] No syncRunId in response:', res.data);
                                        }

                                        if (syncJobsWait && syncRunId) {
                                          await pollJobBySyncRunId(syncRunId, { timeoutSeconds: syncJobsWaitTimeoutSeconds });
                                        }
                                        await fetchSyncJobs();
                                        return res.data;
                                      })
                                    }
                                  >
                                    Start{syncJobsWait ? ' & wait' : ''}
                                  </Button>
                                </span>
                              } />
                            </Stack>
                          </Stack>

                          {running ? (
                            <Stack spacing={0.5}>
                              <LinearProgress
                                variant={typeof progressNumber === 'number' ? 'determinate' : 'indeterminate'}
                                value={typeof progressNumber === 'number' ? progressNumber : undefined}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {typeof progressNumber === 'number' ? `Progress: ${Math.round(progressNumber)}%` : 'In progress...'}
                              </Typography>
                            </Stack>
                          ) : null}

                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                            <Typography variant="caption" color="text.secondary">
                              Last completed: {lastCompleted || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last failed: {lastFailed || '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Schedule env: {envEveryMinutesKey || '—'}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {/* Live Sync Progress */}
      <Collapse in={!!activeSyncRunId && showLiveProgress}>
        <Card variant="outlined" sx={{ borderColor: sseConnected ? 'success.main' : 'grey.400', borderWidth: 2 }}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Live Sync Progress
                  </Typography>
                  {sseConnected ? (
                    <Chip size="small" label="Live" color="success" variant="filled" />
                  ) : sseError ? (
                    <Chip size="small" label="Disconnected" color="error" variant="outlined" />
                  ) : (
                    <Chip size="small" label="Connecting..." color="warning" variant="outlined" />
                  )}
                  {syncCompleted && (
                    <Chip size="small" label="Completed" color="info" variant="filled" />
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      disconnectSse();
                      setActiveSyncRunId(null);
                    }}
                  >
                    Close
                  </Button>
                  <Button size="small" variant="text" onClick={() => setShowLiveProgress(false)}>
                    Hide
                  </Button>
                </Stack>
              </Stack>

              {sseError && (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  SSE connection error. Polling fallback is still active.
                </Alert>
              )}

              {activeSyncRunId && (
                <Typography variant="caption" color="text.secondary">
                  syncRunId: {activeSyncRunId}
                </Typography>
              )}

              {/* Current Phase Progress */}
              {latestPhase && (
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={600}>
                    Phase: {latestPhase}
                  </Typography>
                  {latestProgress && (
                    <Stack spacing={0.5}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.round((latestProgress.progress.current / latestProgress.progress.total) * 100)}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {latestProgress.organization} ({latestProgress.progress.current}/{latestProgress.progress.total})
                        {latestProgress.progress.upserted !== undefined && ` • ${latestProgress.progress.upserted} upserted`}
                        {latestProgress.progress.errors !== undefined && latestProgress.progress.errors > 0 && ` • ${latestProgress.progress.errors} skipped`}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              )}

              {/* Event Log */}
              <Box
                ref={liveProgressRef}
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  p: 1,
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                }}
              >
                {liveEvents.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    Waiting for events...
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {liveEvents.map((event, idx) => {
                      const time = new Date(event.timestamp).toLocaleTimeString();
                      const phaseEvent = event as SyncPhaseCompleteEvent;

                      if (event.type === 'phase.start') {
                        return (
                          <Typography key={idx} variant="caption" sx={{ color: 'info.main' }}>
                            [{time}] ▶ Starting phase: {(event as any).phase}
                          </Typography>
                        );
                      }

                      if (event.type === 'phase.complete') {
                        const color = phaseEvent.result === 'SUCCESS' ? 'success.main' : phaseEvent.result === 'FAILED' ? 'error.main' : 'warning.main';
                        return (
                          <Typography key={idx} variant="caption" sx={{ color }}>
                            [{time}] ✓ Phase {phaseEvent.phase}: {phaseEvent.result} ({Math.round(phaseEvent.durationMs / 1000)}s)
                            {phaseEvent.error && ` - ${phaseEvent.error}`}
                          </Typography>
                        );
                      }

                      if (event.type === 'phase.progress') {
                        const progressEvent = event as any;
                        return (
                          <Typography key={idx} variant="caption" sx={{ color: 'text.secondary' }}>
                            [{time}] • {progressEvent.phase}/{progressEvent.organization}: {progressEvent.progress.current}/{progressEvent.progress.total}
                            {progressEvent.progress.upserted !== undefined && ` (${progressEvent.progress.upserted} upserted)`}
                          </Typography>
                        );
                      }

                      if (event.type === 'sync.complete') {
                        const syncEvent = event as any;
                        const color = syncEvent.success ? 'success.main' : 'error.main';
                        return (
                          <Typography key={idx} variant="caption" sx={{ color, fontWeight: 700 }}>
                            [{time}] ★ Sync complete: {syncEvent.success ? 'SUCCESS' : 'FAILED'} ({Math.round(syncEvent.durationMs / 1000)}s total)
                          </Typography>
                        );
                      }

                      return null;
                    })}
                  </Stack>
                )}
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Collapse>

      {/* Show button to re-open live progress if hidden but sync is still running */}
      {activeSyncRunId && !showLiveProgress && !syncCompleted && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowLiveProgress(true)}
          sx={{ alignSelf: 'flex-start' }}
        >
          Show Live Progress
        </Button>
      )}

      {activeTab === 'tools' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Import workers
              </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="status" value={importStatus} onChange={e => setImportStatus(e.target.value)} fullWidth />
              <TextField label="locatie" value={importLocatie} onChange={e => setImportLocatie(e.target.value)} fullWidth />
            </Stack>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="startDate (ISO)" value={importStartDate} onChange={e => setImportStartDate(e.target.value)} fullWidth />
              <TextField label="endDate (ISO)" value={importEndDate} onChange={e => setImportEndDate(e.target.value)} fullWidth />
            </Stack>
            <Button
              variant="contained"
              onClick={() => {
                if (importStartDate) {
                  const twoWeeksAgo = new Date();
                  twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
                  twoWeeksAgo.setHours(0, 0, 0, 0);
                  const parsed = new Date(importStartDate);
                  if (!isNaN(parsed.getTime()) && parsed < twoWeeksAgo) {
                    setStartDateWarnOpen(true);
                    return;
                  }
                }
                runImport();
              }}
              disabled={status === 'loading'}
            >
              Import
            </Button>
          </Stack>
        </CardContent>
      </Card>
      ) : null}

      {activeTab === 'cache' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Cache inspector (meta=1)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Uses the universal freshness contract (`freshness`, `maxStaleSeconds`, `budgetMs`) and always requests `meta=1`.
              </Typography>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                select
                label="Endpoint"
                value={readEndpoint}
                onChange={e => setReadEndpoint(e.target.value as any)}
                fullWidth
              >
                <MenuItem value="reference-cache">/easyflex/reference-cache</MenuItem>
                <MenuItem value="timecards">/easyflex/timecards</MenuItem>
              </TextField>
              <TextField
                label="organization (optional)"
                value={readOrganization}
                onChange={e => setReadOrganization(e.target.value)}
                fullWidth
              />
              <TextField
                select
                label="freshness"
                value={readFreshness}
                onChange={e => setReadFreshness(e.target.value as any)}
                fullWidth
              >
                <MenuItem value="cached">cached</MenuItem>
                <MenuItem value="refresh_async">refresh_async</MenuItem>
                <MenuItem value="refresh_sync">refresh_sync</MenuItem>
              </TextField>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="maxStaleSeconds (optional)"
                value={readMaxStaleSeconds}
                onChange={e => setReadMaxStaleSeconds(e.target.value)}
                fullWidth
              />
              <TextField
                label="budgetMs (optional; timecards)"
                value={readBudgetMs}
                onChange={e => setReadBudgetMs(e.target.value)}
                fullWidth
              />
            </Stack>

            {readEndpoint === 'reference-cache' ? (
              <TextField
                select
                label="kind"
                value={readReferenceKind}
                onChange={e => setReadReferenceKind(e.target.value)}
                fullWidth
              >
                <MenuItem value="inschrijver">inschrijver</MenuItem>
                <MenuItem value="relatiebeheerder">relatiebeheerder</MenuItem>
                <MenuItem value="locatie">locatie</MenuItem>
                <MenuItem value="businessunit">businessunit</MenuItem>
              </TextField>
            ) : (
              <TextField
                label="workerRegistrationNumber (optional)"
                value={readTimecardsWorkerRegistrationNumber}
                onChange={e => setReadTimecardsWorkerRegistrationNumber(e.target.value)}
                fullWidth
              />
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Button
                variant="contained"
                disabled={status === 'loading'}
                onClick={() => runCacheInspectorFetch()}
                data-testid="cache-inspector-fetch"
              >
                Fetch
              </Button>
              <Button
                variant="outlined"
                disabled={status === 'loading'}
                onClick={() => setReadFreshness('refresh_async')}
              >
                Set refresh_async
              </Button>
              <Button
                variant="outlined"
                disabled={status === 'loading'}
                onClick={() => setReadFreshness('refresh_sync')}
              >
                Set refresh_sync
              </Button>
              <Button
                variant="text"
                disabled={status === 'loading'}
                onClick={() => setReadFreshness('cached')}
              >
                Set cached
              </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                disabled={status === 'loading'}
                onClick={() => {
                  setReadFreshness('cached');
                  return runCacheInspectorFetch('cached');
                }}
              >
                Fetch cached
              </Button>
              <Button
                variant="outlined"
                disabled={status === 'loading'}
                onClick={() => {
                  setReadFreshness('refresh_async');
                  return runCacheInspectorFetch('refresh_async');
                }}
              >
                Fetch refresh_async
              </Button>
              <Button
                variant="outlined"
                disabled={status === 'loading'}
                onClick={() => {
                  setReadFreshness('refresh_sync');
                  return runCacheInspectorFetch('refresh_sync');
                }}
              >
                Fetch refresh_sync
              </Button>
            </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'tools' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Quick actions
              </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Button
                variant="outlined"
                onClick={() =>
                  run(async () => {
                    const res = await centralDbRequest({ method: 'GET', path: '/easyflex/sync-issues', token: accessToken });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                    return res.data;
                  })
                }
                disabled={status === 'loading'}
              >
                Get sync issues
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  run(async () => {
                    const res = await centralDbRequest({ method: 'POST', path: '/easyflex/sync-pending', token: accessToken });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                    return res.data;
                  })
                }
                disabled={status === 'loading'}
              >
                Sync pending
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  run(async () => {
                    const res = await centralDbRequest({
                      method: 'POST',
                      path: '/easyflex/sync/full',
                      token: accessToken,
                      body: { autoResolveConflicts: true },
                    });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                    return res.data;
                  })
                }
                disabled={status === 'loading'}
              >
                Full sync
              </Button>
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Canonical data-sync jobs
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap">
              {(['all', 'workers', 'placements', 'businesses', 'projects', 'hours', 'timecards', 'reference', 'tariffs'] as const).map(key => (
                <Button
                  key={key}
                  variant="outlined"
                  onClick={() =>
                    run(async () => {
                      const orgs = syncJobsOrganizations
                        .split(',')
                        .map(s => s.trim())
                        .filter(Boolean);
                      const res = await centralDbRequest({
                        method: 'POST',
                        path: `/easyflex/syncs/${encodeURIComponent(key)}/start`,
                        token: accessToken,
                        body: orgs.length ? { organizations: orgs } : {},
                      });
                      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                      const syncRunId = String(res.data?.syncRunId ?? '').trim();
                      if (syncJobsWait && syncRunId) {
                        await pollJobBySyncRunId(syncRunId, { timeoutSeconds: syncJobsWaitTimeoutSeconds });
                      }
                      await Promise.all([fetchSyncJobs(), fetchStats(), fetchRecentErrors()]);
                      return res.data;
                    })
                  }
                  disabled={status === 'loading'}
                >
                  Sync {key}
                  {syncJobsWait ? ' & wait' : ''}
                </Button>
              ))}
            </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'tools' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Conflicts & not-synced workers
                </Typography>
                <Button variant="outlined" onClick={() => fetchSyncIssues()} disabled={syncIssuesLoading}>
                  Refresh
                </Button>
              </Stack>

            {syncIssuesError ? <Alert severity="error">{syncIssuesError}</Alert> : null}
            {syncIssuesLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Chip label={`conflicts=${Number(syncIssues?.conflicts?.length ?? 0)}`} color={syncIssues?.conflicts?.length ? 'warning' : 'default'} />
              <Chip label={`errors=${Number(syncIssues?.errors?.length ?? 0)}`} color={syncIssues?.errors?.length ? 'error' : 'default'} />
              <Chip label={`notSynced=${Number(syncIssues?.notSynced?.length ?? 0)}`} />
              <Chip label={`total=${Number(syncIssues?.total ?? 0)}`} variant="outlined" />
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Conflicts (limited to 100)
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(syncIssues?.conflicts) && syncIssues.conflicts.length ? (
                syncIssues.conflicts.map((w: any, idx: number) => {
                  const id = String(w?.id ?? idx);
                  const reg = w?.easyflexRegistrationNumber ? String(w.easyflexRegistrationNumber) : '';
                  const org = w?.organizationName ? String(w.organizationName) : '';
                  return (
                    <Card key={id} variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                            <Chip size="small" label={`workerId=${id}`} variant="outlined" />
                            {org ? <Chip size="small" label={org} variant="outlined" /> : null}
                            {reg ? <Chip size="small" label={`reg=${reg}`} variant="outlined" /> : null}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setWorkerId(id);
                              }}
                            >
                              Prefill worker sync
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                run(async () => {
                                  const res = await centralDbRequest({
                                    method: 'POST',
                                    path: `/easyflex/workers/${encodeURIComponent(id)}/sync`,
                                    token: accessToken,
                                    body: { direction: 'EASYFLEX_TO_LOCAL', resolveConflicts: true, autoResolve: true, forceSync: true },
                                  });
                                  if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                                  await fetchSyncIssues();
                                  await fetchRecentErrors();
                                  return res.data;
                                })
                              }
                              disabled={status === 'loading'}
                            >
                              Auto-resolve & sync
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSyncLogsWorkerId(id);
                                setSyncLogsOffset(0);
                                setSelectedSyncLog(null);
                                fetchSyncLogs({ offset: 0 });
                              }}
                            >
                              Show logs
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No conflicted workers returned.
                </Typography>
              )}
            </Stack>

            <Divider />

            <Typography variant="subtitle2" fontWeight={700}>
              Not synced (limited to 100)
            </Typography>
            <Stack spacing={1}>
              {Array.isArray(syncIssues?.notSynced) && syncIssues.notSynced.length ? (
                syncIssues.notSynced.map((w: any, idx: number) => {
                  const id = String(w?.id ?? idx);
                  const reg = w?.easyflexRegistrationNumber ? String(w.easyflexRegistrationNumber) : '';
                  const org = w?.organizationName ? String(w.organizationName) : '';
                  return (
                    <Card key={id} variant="outlined">
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                            <Chip size="small" label={`workerId=${id}`} variant="outlined" />
                            {org ? <Chip size="small" label={org} variant="outlined" /> : null}
                            {reg ? <Chip size="small" label={`reg=${reg}`} variant="outlined" /> : null}
                            <Button size="small" variant="outlined" onClick={() => setWorkerId(id)}>
                              Prefill worker sync
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() =>
                                run(async () => {
                                  const res = await centralDbRequest({
                                    method: 'POST',
                                    path: `/easyflex/workers/${encodeURIComponent(id)}/sync`,
                                    token: accessToken,
                                    body: { direction: 'EASYFLEX_TO_LOCAL', resolveConflicts: true, autoResolve: true, forceSync: true },
                                  });
                                  if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                                  await fetchSyncIssues();
                                  await fetchRecentErrors();
                                  return res.data;
                                })
                              }
                              disabled={status === 'loading'}
                            >
                              Sync now
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                setSyncLogsWorkerId(id);
                                setSyncLogsOffset(0);
                                setSelectedSyncLog(null);
                                fetchSyncLogs({ offset: 0 });
                              }}
                            >
                              Show logs
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No not-synced workers returned.
                </Typography>
              )}
            </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'tools' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Worker sync/status
              </Typography>
              <TextField label="Worker ID (UUID)" value={workerId} onChange={e => setWorkerId(e.target.value)} fullWidth />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <TextField label="direction" value={direction} onChange={e => setDirection(e.target.value)} fullWidth />
                <FormControlLabel control={<Checkbox checked={forceSync} onChange={e => setForceSync(e.target.checked)} />} label="forceSync" />
                <FormControlLabel
                  control={<Checkbox checked={resolveConflicts} onChange={e => setResolveConflicts(e.target.checked)} />}
                  label="resolveConflicts"
                />
                <FormControlLabel control={<Checkbox checked={autoResolve} onChange={e => setAutoResolve(e.target.checked)} />} label="autoResolve" />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    run(async () => {
                      if (!workerId) throw new Error('Worker ID is required');
                      const res = await centralDbRequest({
                        method: 'GET',
                        path: `/easyflex/workers/${workerId}/sync-status`,
                        token: accessToken,
                      });
                      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                      return res.data;
                    })
                  }
                  disabled={status === 'loading'}
                >
                  Get sync status
                </Button>
                <Button
                  variant="contained"
                  onClick={() =>
                    run(async () => {
                      if (!workerId) throw new Error('Worker ID is required');
                      const res = await centralDbRequest({
                        method: 'POST',
                        path: `/easyflex/workers/${workerId}/sync`,
                        token: accessToken,
                        body: optionsPayload,
                      });
                      if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                      return res.data;
                    })
                  }
                  disabled={status === 'loading'}
                >
                  Trigger sync
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'tools' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Bulk sync
              </Typography>
              <TextField
                label="Worker IDs (one per line)"
                value={bulkWorkerIds}
                onChange={e => setBulkWorkerIds(e.target.value)}
                fullWidth
                multiline
                minRows={4}
              />
              <Button
                variant="contained"
                onClick={() =>
                  run(async () => {
                    const ids = bulkWorkerIds
                      .split('\n')
                      .map(s => s.trim())
                      .filter(Boolean);
                    if (!ids.length) throw new Error('Provide at least one worker id');
                    const res = await centralDbRequest({
                      method: 'POST',
                      path: '/easyflex/bulk-sync',
                      token: accessToken,
                      body: { workerIds: ids, options: optionsPayload },
                    });
                    if (!res.ok) throw new Error(typeof res.data === 'string' ? res.data : `HTTP ${res.status}`);
                    return res.data;
                  })
                }
                disabled={status === 'loading'}
              >
                Start bulk sync
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'collisions' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Collisions
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setCollisionsOffset(0);
                    setSelectedCollision(null);
                    fetchCollisions({ offset: 0 });
                  }}
                  disabled={collisionsLoading}
                >
                  Refresh
                </Button>
              </Stack>

            {collisionsError ? <Alert severity="error">{collisionsError}</Alert> : null}
            {collisionsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="type" value={collisionsType} onChange={e => setCollisionsType(e.target.value)} fullWidth>
                <MenuItem value="">(any)</MenuItem>
                <MenuItem value="WORKER_REGISTRATION_NUMBER">WORKER_REGISTRATION_NUMBER</MenuItem>
                <MenuItem value="PLACEMENT_NUMBER">PLACEMENT_NUMBER</MenuItem>
              </TextField>
              <TextField
                label="externalId"
                value={collisionsExternalId}
                onChange={e => setCollisionsExternalId(e.target.value)}
                fullWidth
              />
              <TextField
                label="organization"
                value={collisionsOrganization}
                onChange={e => setCollisionsOrganization(e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="since (ISO)"
                value={collisionsSince}
                onChange={e => setCollisionsSince(e.target.value)}
                placeholder="2026-01-01T00:00:00Z"
                fullWidth
              />
              <TextField
                label="until (ISO)"
                value={collisionsUntil}
                onChange={e => setCollisionsUntil(e.target.value)}
                placeholder="2026-01-02T00:00:00Z"
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <FormControlLabel
                control={<Checkbox checked={collisionsIncludeImpacted} onChange={e => setCollisionsIncludeImpacted(e.target.checked)} />}
                label="Include impacted entities"
              />
              <TextField
                label="limit"
                value={String(collisionsLimit)}
                onChange={e => setCollisionsLimit(Number(e.target.value || 50))}
                sx={{ width: { md: 140 } }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setCollisionsOffset(0);
                  setSelectedCollision(null);
                  fetchCollisions({ offset: 0, limit: collisionsLimit });
                }}
                disabled={collisionsLoading}
              >
                Apply filters
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = Math.max(collisionsOffset - collisionsLimit, 0);
                    setCollisionsOffset(nextOffset);
                    setSelectedCollision(null);
                    fetchCollisions({ offset: nextOffset });
                  }}
                  disabled={collisionsLoading || collisionsOffset <= 0}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = collisionsOffset + collisionsLimit;
                    setCollisionsOffset(nextOffset);
                    setSelectedCollision(null);
                    fetchCollisions({ offset: nextOffset });
                  }}
                  disabled={collisionsLoading || (collisionsTotal ? collisionsOffset + collisionsLimit >= collisionsTotal : false)}
                >
                  Next
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Total: {collisionsTotal}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={collisionsAutoRefresh} onChange={e => setCollisionsAutoRefresh(e.target.checked)} />}
                label="Auto-refresh collisions"
              />
            </Stack>

            {Array.isArray(collisions) ? (
              <Stack spacing={1}>
                {collisions.length ? (
                  collisions.map((evt, idx) => {
                    const id = String(evt?.id || idx);
                    const createdAt = evt?.createdAt ? new Date(evt.createdAt).toLocaleString() : '—';
                    const type = String(evt?.collisionType || '—');
                    const externalId = evt?.externalId ?? '—';
                    const orgs = Array.isArray(evt?.organizations) ? evt.organizations.join(', ') : '';
                    const impactedWorkers = Array.isArray(evt?.impacted?.workers) ? evt.impacted.workers.length : 0;
                    const impactedPlacements = Array.isArray(evt?.impacted?.placements) ? evt.impacted.placements.length : 0;

                    return (
                      <Card key={id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {createdAt}
                              </Typography>
                              <Chip size="small" label={type} />
                              <Chip size="small" variant="outlined" label={`externalId=${externalId}`} />
                              {orgs ? <Chip size="small" variant="outlined" label={orgs} /> : null}
                              {collisionsIncludeImpacted && impactedWorkers ? (
                                <Chip size="small" variant="outlined" label={`workers=${impactedWorkers}`} />
                              ) : null}
                              {collisionsIncludeImpacted && impactedPlacements ? (
                                <Chip size="small" variant="outlined" label={`placements=${impactedPlacements}`} />
                              ) : null}
                              <Button size="small" variant="outlined" onClick={() => setSelectedCollision(evt)}>
                                Details
                              </Button>
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No collisions found for the current filters.
                  </Typography>
                )}
              </Stack>
            ) : null}

            {selectedCollision ? (
              <Stack spacing={1}>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  Selected collision
                </Typography>
                <TextField value={jsonStringify(selectedCollision)} fullWidth multiline minRows={6} />
              </Stack>
            ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'jobs' ? (
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Sync runs
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSyncRunsOffset(0);
                    setSelectedSyncRun(null);
                    setSelectedSyncRunEvents(null);
                    fetchSyncRuns({ offset: 0 });
                  }}
                  disabled={syncRunsLoading}
                >
                  Refresh
                </Button>
              </Stack>

            {syncRunsError ? <Alert severity="error">{syncRunsError}</Alert> : null}
            {syncRunsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="status" value={syncRunsStatus} onChange={e => setSyncRunsStatus(e.target.value)} fullWidth>
                <MenuItem value="">(any)</MenuItem>
                <MenuItem value="RUNNING">RUNNING</MenuItem>
                <MenuItem value="COMPLETED">COMPLETED</MenuItem>
                <MenuItem value="FAILED">FAILED</MenuItem>
              </TextField>
              <TextField label="key" value={syncRunsKey} onChange={e => setSyncRunsKey(e.target.value)} fullWidth />
              <TextField label="organization" value={syncRunsOrganization} onChange={e => setSyncRunsOrganization(e.target.value)} fullWidth />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField
                label="since (ISO)"
                value={syncRunsSince}
                onChange={e => setSyncRunsSince(e.target.value)}
                placeholder="2026-01-01T00:00:00Z"
                fullWidth
              />
              <TextField
                label="until (ISO)"
                value={syncRunsUntil}
                onChange={e => setSyncRunsUntil(e.target.value)}
                placeholder="2026-01-02T00:00:00Z"
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="limit"
                value={String(syncRunsLimit)}
                onChange={e => setSyncRunsLimit(Number(e.target.value || 50))}
                sx={{ width: { md: 140 } }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setSyncRunsOffset(0);
                  setSelectedSyncRun(null);
                  setSelectedSyncRunEvents(null);
                  fetchSyncRuns({ offset: 0, limit: syncRunsLimit });
                }}
                disabled={syncRunsLoading}
              >
                Apply filters
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = Math.max(syncRunsOffset - syncRunsLimit, 0);
                    setSyncRunsOffset(nextOffset);
                    setSelectedSyncRun(null);
                    setSelectedSyncRunEvents(null);
                    fetchSyncRuns({ offset: nextOffset });
                  }}
                  disabled={syncRunsLoading || syncRunsOffset <= 0}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = syncRunsOffset + syncRunsLimit;
                    setSyncRunsOffset(nextOffset);
                    setSelectedSyncRun(null);
                    setSelectedSyncRunEvents(null);
                    fetchSyncRuns({ offset: nextOffset });
                  }}
                  disabled={syncRunsLoading || (syncRunsTotal ? syncRunsOffset + syncRunsLimit >= syncRunsTotal : false)}
                >
                  Next
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Total: {syncRunsTotal}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={syncRunsAutoRefresh} onChange={e => setSyncRunsAutoRefresh(e.target.checked)} />}
                label="Auto-refresh sync runs"
              />
            </Stack>

            {Array.isArray(syncRuns) ? (
              <Stack spacing={1}>
                {syncRuns.length ? (
                  syncRuns.map((r, idx) => {
                    const id = String(r?.syncRunId || idx);
                    const createdAt = r?.createdAt ? new Date(r.createdAt).toLocaleString() : '—';
                    const status = String(r?.status || '—');
                    const key = r?.key ? String(r.key) : '';
                    const orgs = Array.isArray(r?.organizations) ? r.organizations.join(', ') : '';
                    const errorMessage = r?.errorMessage ? String(r.errorMessage) : '';

                    return (
                      <Card key={id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {createdAt}
                              </Typography>
                              <Chip size="small" label={status} color={status === 'FAILED' ? 'error' : status === 'RUNNING' ? 'warning' : 'success'} />
                              {key ? <Chip size="small" variant="outlined" label={key} /> : null}
                              {orgs ? <Chip size="small" variant="outlined" label={orgs} /> : null}
                              <Chip size="small" variant="outlined" label={`syncRunId=${id}`} />
                              <Button size="small" variant="outlined" onClick={() => fetchSyncRunDetail(id)}>
                                Run detail
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setSyncLogsSyncRunId(id);
                                  setSyncLogsOffset(0);
                                  setSelectedSyncLog(null);
                                  const p = new URLSearchParams(searchParams);
                                  p.set('tab', 'logs');
                                  p.set('syncRunId', id);
                                  setSearchParams(p, { replace: true });
                                  fetchSyncLogs({ offset: 0, queryOverride: { syncRunId: id } as any });
                                }}
                              >
                                Filter logs
                              </Button>
                            </Stack>
                            {errorMessage ? (
                              <Typography variant="body2" color="error">
                                {errorMessage}
                              </Typography>
                            ) : null}
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No sync runs found for the current filters.
                  </Typography>
                )}
              </Stack>
            ) : null}

            {selectedSyncRunDetailError ? <Alert severity="error">{selectedSyncRunDetailError}</Alert> : null}
            {selectedSyncRunDetailLoading ? <LinearProgress /> : null}

            {selectedSyncRunEvents ? (
              <Stack spacing={1}>
                <Divider />
                <Typography variant="subtitle2" fontWeight={700}>
                  Selected run events
                </Typography>
                {selectedSyncRun ? (
                  <Typography variant="caption" color="text.secondary">
                    syncRunId={String(selectedSyncRun?.syncRunId ?? selectedSyncRunEvents?.syncRunId ?? '')} status=
                    {String(selectedSyncRun?.status ?? '')} key={String(selectedSyncRun?.key ?? '')} orgs=
                    {Array.isArray(selectedSyncRun?.organizations) ? selectedSyncRun.organizations.join(',') : String(selectedSyncRun?.organization ?? '')}
                  </Typography>
                ) : null}
                <Typography variant="caption" color="text.secondary">
                  logs={Number(selectedSyncRunEvents?.logs?.total ?? 0)} collisions={Number(selectedSyncRunEvents?.collisions?.total ?? 0)}
                </Typography>
                <TextField value={jsonStringify(selectedSyncRunEvents)} fullWidth multiline minRows={10} />
              </Stack>
            ) : null}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      {activeTab === 'logs' ? (
        <Card variant="outlined">
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Typography variant="subtitle1" fontWeight={700}>
                Sync logs
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSyncLogsOffset(0);
                    setSelectedSyncLog(null);
                    fetchSyncLogs({ offset: 0 });
                  }}
                  disabled={syncLogsLoading}
                >
                  Refresh
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    run(async () => {
                      const p = new URLSearchParams(searchParams);
                      p.set('tab', 'logs');
                      if (syncLogsSyncRunId) p.set('syncRunId', syncLogsSyncRunId);
                      else p.delete('syncRunId');
                      const url = `${window.location.origin}${window.location.pathname}?${p.toString()}`;
                      await copyToClipboard(url);
                      return { copied: true, url };
                    })
                  }
                  disabled={status === 'loading'}
                >
                  Copy link
                </Button>
              </Stack>
            </Stack>

            {syncLogsError ? <Alert severity="error">{syncLogsError}</Alert> : null}
            {syncLogsLoading ? <LinearProgress /> : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="workerId (uuid)" value={syncLogsWorkerId} onChange={e => setSyncLogsWorkerId(e.target.value)} fullWidth />
              <TextField
                label="easyflexRegistrationNumber"
                value={syncLogsEasyflexRegistrationNumber}
                onChange={e => setSyncLogsEasyflexRegistrationNumber(e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                <TextField
                  label="syncRunId (uuid)"
                  value={syncLogsSyncRunId}
                  onChange={e => setSyncLogsSyncRunId(e.target.value)}
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    const id = String(syncLogsSyncRunId ?? '').trim();
                    if (!id) return;
                    fetchSyncRunDetail(id);
                  }}
                  disabled={selectedSyncRunDetailLoading || !String(syncLogsSyncRunId ?? '').trim()}
                >
                  Load run detail
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    setSelectedSyncRun(null);
                    setSelectedSyncRunEvents(null);
                    setSelectedSyncRunDetailError(null);
                  }}
                  disabled={!selectedSyncRun && !selectedSyncRunEvents && !selectedSyncRunDetailError}
                >
                  Clear detail
                </Button>
              </Stack>
              <TextField
                label="organizationName"
                value={syncLogsOrganizationName}
                onChange={e => setSyncLogsOrganizationName(e.target.value)}
                fullWidth
              />
              <TextField label="entity" value={syncLogsEntity} onChange={e => setSyncLogsEntity(e.target.value)} fullWidth />
            </Stack>

            {selectedSyncRunDetailError ? <Alert severity="error">{selectedSyncRunDetailError}</Alert> : null}
            {selectedSyncRunDetailLoading ? <LinearProgress /> : null}
            {selectedSyncRunEvents ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        Sync run detail
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => copyToClipboard(jsonStringify({ run: selectedSyncRun, events: selectedSyncRunEvents }))}
                      >
                        Copy JSON
                      </Button>
                    </Stack>
                    {selectedSyncRun ? (
                      <Typography variant="caption" color="text.secondary">
                        syncRunId={String(selectedSyncRun?.syncRunId ?? selectedSyncRunEvents?.syncRunId ?? '')} status=
                        {String(selectedSyncRun?.status ?? '')} key={String(selectedSyncRun?.key ?? '')} orgs=
                        {Array.isArray(selectedSyncRun?.organizations)
                          ? selectedSyncRun.organizations.join(',')
                          : String(selectedSyncRun?.organization ?? '')}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      logs={Number(selectedSyncRunEvents?.logs?.total ?? 0)} collisions={Number(selectedSyncRunEvents?.collisions?.total ?? 0)}
                    </Typography>
                    <TextField value={jsonStringify(selectedSyncRunEvents)} fullWidth multiline minRows={6} />
                  </Stack>
                </CardContent>
              </Card>
            ) : null}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField select label="operation" value={syncLogsOperation} onChange={e => setSyncLogsOperation(e.target.value)} fullWidth>
                <MenuItem value="">(any)</MenuItem>
                <MenuItem value="CREATE">CREATE</MenuItem>
                <MenuItem value="UPDATE">UPDATE</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
                <MenuItem value="FETCH">FETCH</MenuItem>
                <MenuItem value="BULK_IMPORT">BULK_IMPORT</MenuItem>
              </TextField>
              <TextField select label="result" value={syncLogsResult} onChange={e => setSyncLogsResult(e.target.value)} fullWidth>
                <MenuItem value="">(any)</MenuItem>
                <MenuItem value="SUCCESS">SUCCESS</MenuItem>
                <MenuItem value="FAILED">FAILED</MenuItem>
                <MenuItem value="PARTIAL">PARTIAL</MenuItem>
                <MenuItem value="SKIPPED">SKIPPED</MenuItem>
              </TextField>
              <TextField
                label="initiatedBy"
                value={syncLogsInitiatedBy}
                onChange={e => setSyncLogsInitiatedBy(e.target.value)}
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
              <TextField label="q (search)" value={syncLogsQuery} onChange={e => setSyncLogsQuery(e.target.value)} fullWidth />
              <TextField
                label="since (ISO)"
                value={syncLogsSince}
                onChange={e => setSyncLogsSince(e.target.value)}
                placeholder="2026-01-01T00:00:00Z"
                fullWidth
              />
              <TextField
                label="until (ISO)"
                value={syncLogsUntil}
                onChange={e => setSyncLogsUntil(e.target.value)}
                placeholder="2026-01-02T00:00:00Z"
                fullWidth
              />
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
              <TextField
                label="limit"
                value={String(syncLogsLimit)}
                onChange={e => setSyncLogsLimit(Number(e.target.value || 50))}
                sx={{ width: { md: 140 } }}
              />
              <Button
                variant="contained"
                onClick={() => {
                  setSyncLogsOffset(0);
                  setSelectedSyncLog(null);
                  fetchSyncLogs({ offset: 0, limit: syncLogsLimit });
                }}
                disabled={syncLogsLoading}
              >
                Apply filters
              </Button>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = Math.max(syncLogsOffset - syncLogsLimit, 0);
                    setSyncLogsOffset(nextOffset);
                    setSelectedSyncLog(null);
                    fetchSyncLogs({ offset: nextOffset });
                  }}
                  disabled={syncLogsLoading || syncLogsOffset <= 0}
                >
                  Prev
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const nextOffset = syncLogsOffset + syncLogsLimit;
                    setSyncLogsOffset(nextOffset);
                    setSelectedSyncLog(null);
                    fetchSyncLogs({ offset: nextOffset });
                  }}
                  disabled={syncLogsLoading || (syncLogsTotal ? syncLogsOffset + syncLogsLimit >= syncLogsTotal : false)}
                >
                  Next
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Total: {syncLogsTotal}
              </Typography>
              <FormControlLabel
                control={<Checkbox checked={syncLogsAutoRefresh} onChange={e => setSyncLogsAutoRefresh(e.target.checked)} />}
                label="Auto-refresh logs"
              />
            </Stack>

            {Array.isArray(syncLogs) ? (
              <Stack spacing={1}>
                {syncLogs.length ? (
                  syncLogs.map((log, idx) => {
                    const id = String(log?.id || idx);
                    const createdAt = log?.createdAt ? new Date(log.createdAt).toLocaleString() : '—';
                    const op = String(log?.operation || '—');
                    const res = String(log?.result || '—');
                    const dir = String(log?.direction || '—');
                    const worker = String(log?.workerId || '');
                    const reg = log?.easyflexRegistrationNumber ?? '';
                    const org = log?.organizationName ? String(log.organizationName) : '';
                    const ent = log?.entity ? String(log.entity) : '';
                    const syncRunId = log?.syncRunId ? String(log.syncRunId) : '';
                    const duration = typeof log?.duration === 'number' ? `${log.duration}ms` : '—';
                    const errorMessage = log?.errorMessage ? String(log.errorMessage) : '';

                    return (
                      <Card key={id} variant="outlined">
                        <CardContent>
                          <Stack spacing={1}>
                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                              <Typography variant="subtitle2" fontWeight={700}>
                                {createdAt}
                              </Typography>
                              <Chip size="small" label={op} />
                              <Chip size="small" color={res === 'SUCCESS' ? 'success' : res === 'FAILED' ? 'error' : 'default'} label={res} />
                              <Chip size="small" variant="outlined" label={dir} />
                              {ent ? <Chip size="small" variant="outlined" label={ent} /> : null}
                              {org ? <Chip size="small" variant="outlined" label={org} /> : null}
                              {syncRunId ? <Chip size="small" variant="outlined" label={`syncRunId=${syncRunId}`} /> : null}
                              <Typography variant="caption" color="text.secondary">
                                {duration}
                              </Typography>
                              {syncRunId ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    const id = String(syncRunId).trim();
                                    setSyncLogsSyncRunId(id);
                                    setSyncLogsOffset(0);
                                    setSelectedSyncLog(null);
                                    const p = new URLSearchParams(searchParams);
                                    p.set('tab', 'logs');
                                    p.set('syncRunId', id);
                                    setSearchParams(p);
                                    fetchSyncLogs({ offset: 0, queryOverride: { syncRunId: id } as any });
                                    fetchSyncRunDetail(id);
                                  }}
                                >
                                  Run detail
                                </Button>
                              ) : null}
                              <Button size="small" variant="outlined" onClick={() => setSelectedSyncLog(log)}>
                                Details
                              </Button>
                            </Stack>

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                              <Typography variant="caption" color="text.secondary">
                                workerId: {worker || '—'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                easyflexRegistrationNumber: {reg || '—'}
                              </Typography>
                              {errorMessage ? (
                                <Typography variant="caption" color="error">
                                  {errorMessage.slice(0, 200)}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Stack>
                        </CardContent>
                      </Card>
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No logs found.
                  </Typography>
                )}
              </Stack>
            ) : null}

            {selectedSyncLog ? (
              <Card variant="outlined">
                <CardContent>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2" color="text.secondary">
                      Selected log
                    </Typography>
                    <Button size="small" variant="text" onClick={() => setSelectedSyncLog(null)}>
                      Clear
                    </Button>
                  </Stack>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(selectedSyncLog)}</pre>
                </CardContent>
              </Card>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
      ) : null}

      <Divider />

      <SaleslistActieflijstWeekSyncSection accessToken={accessToken} busy={status === 'loading'} run={run} />

      <Divider />

      {status === 'error' ? <Alert severity="error">{error}</Alert> : null}
      {status === 'done' ? <Alert severity="success">Done.</Alert> : null}
      {result ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary">
              Result
            </Typography>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{jsonStringify(result)}</pre>
          </CardContent>
        </Card>
      ) : null}
    </Stack>
  );
};

export default EasyflexSyncPage;
